/**
 * OfficerEditPopup — modal for editing a single officer's assignments.
 *
 * Opened when a user clicks a cell on the canvas. Shows the full officer
 * card with the clicked assignment row highlighted in blue.
 * Saving calls updateOfficerAssignments (one atomic history entry) then triggers
 * onSaved() which causes the parent to refetch and repaint the canvas without
 * any scroll jump.
 */

import { defaultNewRowRange } from '@/utils/newCounterRow';
import { UI_SCALE } from '@/utils/uiScale';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/api/client';
import { availabilityNews, rowsChangedRoster } from '@/utils/savedChange';
import { useNotification, SAVED_PREFIX } from '@/contexts/NotificationContext';
import { useSession } from '@/contexts/SessionContext';
import { ViewOnlyBanner } from '@/components/ViewOnlyBanner';
import { queueSave, flushSaves } from '@/utils/saveQueue';
import { timingSpan } from '@/utils/partialTiming';
import { greenBlocks, type BreakOverrides } from '@/utils/unassignedOfficers';
import { CounterChoiceSelect } from '@/components/CounterChoiceSelect';
import { counterVacancyOnGrid } from '@/utils/counterVacancyFromGrid';
import { announceCounterFocus } from '@/utils/autoFollowCounter';
import { exportSlotToTime } from '@/components/tabs/home/gridData';
import type { CounterVacancy } from '@/utils/hwCounterVacancy';
import { DurationPill } from '@/components/DurationPill';
import { WINDOW_COLORS } from '@/utils/windowDuration';
import { choiceOf, parseCounterChoice } from '@/utils/counterChoice';
import { MaxCounterDurationField } from '@/components/MaxCounterDurationField';
import { HandoffOverlapToggle } from '@/components/HandoffOverlapToggle';
import { WaIcon, WhatsAppQrStep } from '@/components/WhatsAppShared';
import type { RecipientPill } from '@/components/WhatsAppShared';
import type { ContactList } from '@/types/api';
import { ContactPicker } from '@/components/ContactPicker';
import { DEFAULT_WHATSAPP_FOOTER, useWhatsAppFooter } from '@/utils/whatsappFooter';
import {
  generateTimeSlots,
  getStartHour,
  isValidTimeRange,
  hmmmToSlot,
  hmmmToEndSlot,
  formatCounterLabel,
  getDurationPalette,
  getBreakOptions,
} from '@/config/modeConfig';
import { slotToHhmm } from '@/utils/partialTiming';
import { mergeAvailabilitySegments, officerWindows, rowsInWindow, widenWindowsToCover, windowsCoveringRows } from '@/utils/availabilityWindows';
import { BreakDurationBadge } from '@/components/BreakBattery';
import type { RosterGridData, ManualAllocation, OperationResult } from '@/types/api';

function fmtHHMM(t: string) { return `${t.slice(0, 2)}:${t.slice(2)}`; }

const BREAK_OPTIONS = getBreakOptions();

function computeDefaultBreakSizes(
  segs: Array<{ start: string; end: string }>,
  shiftType: string,
): (number | null)[] {
  const startHour = getStartHour(shiftType);
  const isNight = shiftType === 'night';
  let total = 0;
  for (const seg of segs) {
    try { total += Math.max(0, hmmmToEndSlot(seg.end, startHour) - hmmmToSlot(seg.start, startHour)); }
    catch { /* skip bad segments */ }
  }
  if (total >= 36) return isNight ? [10, 2, 2] : [2, 3, 3];
  if (total >= 28) return [2, 3, null];
  if (total >= 20) return [3, null, null];
  if (total >= 11) return [2, null, null];
  return [null, null, null];
}

// A freshly added row has no counter until the user picks one — it must not be
// saved (or shown in schedule text) as a real assignment.
/** A row the server holds: a counter, or a free-text duty (counter "0", text in `duty`). */
const hasCounter = (r: { counter: string; duty?: string }) => !!r.duty || (r.counter !== '' && r.counter !== '0');

// The green (available-but-unassigned) time auto-assign will fill, as HHMM ranges. Uses
// the same rule as the officer grid and the backend (greenBlocks), so a short gap the grid
// shows as a break is not offered, and every range offered here is one the backend fills.
function computeVacantRanges(
  availSegs: Array<{ start: string; end: string }>,
  assignments: Array<{ startTime: string; endTime: string; counter: string }>,
  startHour: number,
  timeSlots: string[],
  overrides?: { cleared?: number[]; userBreaks?: number[] },
): Array<{ start: string; end: string }> {
  const schedule: number[] = new Array(timeSlots.length).fill(0);
  for (const row of assignments) {
    if (!row.counter || row.counter === '0') continue;
    try {
      const s = hmmmToSlot(row.startTime, startHour);
      const e = hmmmToEndSlot(row.endTime, startHour);
      for (let i = s; i < e && i < schedule.length; i++) schedule[i] = parseInt(row.counter, 10) || 1;
    } catch { /* skip bad row */ }
  }
  const segs: Array<[number, number]> = [];
  for (const seg of availSegs) {
    try { segs.push([hmmmToSlot(seg.start, startHour), hmmmToEndSlot(seg.end, startHour)]); } catch { /* skip bad segment */ }
  }
  return greenBlocks(
    schedule, segs,
    overrides?.cleared ? new Set(overrides.cleared) : undefined,
    overrides?.userBreaks ? new Set(overrides.userBreaks) : undefined,
  ).map(([s, e]) => ({ start: timeSlots[s], end: timeSlots[e] }));
}

import {
  deriveAssignments,
  splitAssignmentsAtBoundaries,
  calcDurationMinutes,
  minutesBetween,
  rowId,
} from '@/components/OfficerCard';
import type { AssignmentRow, OfficerCard } from '@/components/OfficerCard';
import { FitText } from '@/components/FitText';

const schedulePreStyle: React.CSSProperties = {
  margin: 0, padding: '14px 16px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#0f172a', userSelect: 'text',
};
import { useIsDesktop } from '@/utils/useIsDesktop';
import { scheduleCounterLine, scheduleGapLines } from '@/utils/scheduleGapLines';
import { useNoReliefView } from '@/contexts/NoReliefContext';

const OT_MIN_FROM_START = 4;
const OT_MIN_FROM_END   = 3;

function computeOTBreakWindowDefaults(
  startTime: string, endTime: string, shiftType: string,
): { earliest: string; latestEnd: string } {
  if (!startTime || !endTime) return { earliest: startTime, latestEnd: endTime };
  try {
    const sh = getStartHour(shiftType);
    const ss = hmmmToSlot(startTime, sh);
    const se = hmmmToEndSlot(endTime, sh);
    return {
      earliest: slotToHhmm(Math.min(ss + OT_MIN_FROM_START, se), sh),
      latestEnd: slotToHhmm(Math.max(ss, se - OT_MIN_FROM_END), sh),
    };
  } catch { return { earliest: startTime, latestEnd: endTime }; }
}

function computeOTBreakSizes(startTime: string, endTime: string, shiftType: string): (number | null)[] {
  if (!startTime || !endTime) return [null, null, null];
  try {
    const isNight = shiftType === 'night';
    const sh = getStartHour(shiftType);
    const n = hmmmToEndSlot(endTime, sh) - hmmmToSlot(startTime, sh) + 4;
    if (n >= 36) return isNight ? [10, 2, 2] : [2, 3, 3];
    if (n >= 28) return [2, 3, null];
    if (n >= 20) return [3, null, null];
    if (n >= 11) return [2, null, null];
    return [null, null, null];
  } catch { return [null, null, null]; }
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/>
      <path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

function CopyIcon({ copied }: { copied: boolean }) {
  if (copied) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    );
  }
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}


// Counter occupancy helper — returns true if another officer occupies this counter
// during the given time range.

interface OfficerEditPopupProps {
  officerId: string;
  /** Clicked time label, e.g. "1030-1200" — used to highlight the matching row. */
  clickedTimeLabel: string;
  gridData: RosterGridData;
  shiftType: string;
  numCounters: number;
  operation_mode: string;
  /** Called after a successful save with the API response, so the parent can patch
   * the grid instantly from `result.snapshot` instead of waiting on a full refetch. */
  onSaved: (result?: Pick<OperationResult, 'snapshot'>) => void;
  onClose: () => void;
  /** When true, renders as a card only (no backdrop/panel wrapper). Panel shell is provided by parent. */
  asCard?: boolean;
  /** Whether this card is currently pinned in the multi-card panel. */
  isPinned?: boolean;
  /** Called when user clicks the pin button. */
  onPin?: () => void;
  /** Called when user clicks the unpin button. */
  onUnpin?: () => void;
  /** When true (asCard=false only), panel slides up from bottom instead of from the right. */
  fromBottom?: boolean;
  /** Called whenever the user edits availability window times so the canvas can preview brackets instantly. */
  onAvailabilityChange?: (segs: Array<{ start: string; end: string }>) => void;
  /** Called on every assignment edit so the counter canvas can preview changes instantly
   *  (no save needed). `vacantNotes` carries notes on vacant (no-counter) ranges — these
   *  aren't AssignmentRows, so they ride alongside rather than inside `assignments`. */
  onPreviewChange?: (assignments: AssignmentRow[], vacantNotes?: Array<{ start: string; end: string; note: string }>) => void;
  /** Called when a row's counter is picked, so the grid can bring that counter's row
   *  (and the row's start slot) into view. Not called for duties or "no counter". */
  onRevealCounter?: (counterNum: number, startSlot: number | null) => void;
  /**
   * True while this officer's row is shown in the grid's availability layer
   * ("Hide All CTR" mode, globally or just this row). Counter rows render
   * read-only — availability windows stay fully editable, since editing those
   * is the point of that mode and there is still only one source of truth for
   * them (officer_metadata), shared with the grid and the y-labels.
   */
  readOnlyCounters?: boolean;
  /** The officer's counters on the initially-generated roster (history[0]); lets Restore grey out when nothing has moved. */
  initialSchedule?: number[];
  /** The officer's counters on the current roster, compared against initialSchedule. */
  currentSchedule?: number[];
  /** Strips this officer out of the grid before removeOfficer's round trip resolves
   * — see handleConfirmDelete. Returns a rollback to call if that call fails. */
  onOptimisticRemove?: (officerKeys: string | string[]) => () => void;
  /** Same, for "clear all counters" — see handleConfirmClearCounters. */
  onOptimisticClearCounters?: (officerKey: string) => () => void;
  /** When set (asCard mode with exactly one card showing), the identity + actions row
   *  (type pill, id, deployment, WhatsApp/copy/pin/trash) portals in here — the shared
   *  panel's own title bar, replacing its generic "Officer Schedule" label and sharing
   *  its row with the maximize/close buttons, saving a row of vertical space. Omitted
   *  (or with more than one card stacked, where a single shared row would be ambiguous)
   *  falls back to rendering the row inline in the card. */
  headerAnchor?: HTMLDivElement | null;
  /** The user's per-slot erase/break choices from the officer grid, so auto-assign fills
   *  exactly the slots the grid paints green. */
  breakOverrides?: BreakOverrides;
}

export function OfficerEditPopup({
  officerId,
  clickedTimeLabel,
  gridData,
  shiftType,
  numCounters,
  operation_mode,
  onSaved,
  onClose,
  asCard = false,
  isPinned = false,
  onPin,
  onUnpin,
  fromBottom = false,
  onAvailabilityChange,
  onPreviewChange,
  onRevealCounter,
  readOnlyCounters = false,
  initialSchedule,
  currentSchedule,
  onOptimisticRemove,
  onOptimisticClearCounters,
  headerAnchor,
  breakOverrides,
}: OfficerEditPopupProps) {
  const { notifySuccess, notifyError } = useNotification();
  const { canEdit } = useSession();
  // Everything below that gates on `readOnlyCounters` (the availability-layer
  // toggle) also gates on this — a view-only tab can't edit counters whichever
  // layer it's looking at. Kept as a local name so every existing check below
  // (bar the prop itself) reads unchanged.
  const readOnlyCounters_ = readOnlyCounters || !canEdit;
  const startHour = getStartHour(shiftType);
  const timeSlots = generateTimeSlots(shiftType);

  // ── Card state ────────────────────────────────────────────────────────────
  // Ref shares the derived assignments so all three useState calls use the same IDs.
  const initialAssignmentsRef = useRef<AssignmentRow[] | null>(null);

  const [card, setCard] = useState<OfficerCard>(() => {
    const meta = gridData.officer_metadata[officerId] ?? (() => {
      const t = officerId.startsWith('OT') ? 'OT' as const
              : officerId.startsWith('S')  ? 'SOS' as const
              : 'MAIN' as const;
      return { name: '', deployment: '', officer_type: t };
    })();
    let assignments = deriveAssignments(officerId, gridData);
    // Grid cells (deriveAssignments's only input) know nothing about availability
    // windows, so a continuous counter run can straddle a boundary a window-split
    // created after the fact. Split it here so each window gets its own row with
    // the counter intact, rather than the whole run landing under one window and
    // leaving the other's slice blank.
    const segs = gridData.officer_metadata[officerId]?.availability_segments;
    if (segs && segs.length > 1) {
      assignments = splitAssignmentsAtBoundaries(assignments, segs, startHour);
    }
    initialAssignmentsRef.current = assignments;
    return { officerId, metadata: meta, assignments };
  });
  const [isDirty, setIsDirty] = useState(false);
  const [dirtyRowIds, setDirtyRowIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmClearCounters, setConfirmClearCounters] = useState(false);
  const [clearingCounters, setClearingCounters] = useState(false);
  // Set when deleting an availability window whose range still holds counter
  // rows — a counter must always live inside some window, so deleting the
  // window has to take those rows with it. Confirmed explicitly rather than
  // silently orphaning them (the previous behavior).
  const [windowDeleteConfirm, setWindowDeleteConfirm] = useState<{
    segIndex: number; affectedRows: AssignmentRow[];
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedSegIdx, setCopiedSegIdx] = useState<number | null>(null);
  // Schedule text shown big for the officer to read / photograph; copying lives inside it.
  const [preview, setPreview] = useState<{ text: string; segIdx?: number } | null>(null);
  const isDesktop = useIsDesktop();
  const [regenerating, setRegenerating] = useState(false);
  // Local edits to notes on vacant (unassigned) ranges, keyed by `${start}-${end}`
  // of the range as currently shown — these aren't AssignmentRows (no counter), so
  // they don't live in card.assignments. Falls back to whatever's already saved
  // (savedVacantNote) until the user types here. See handleSave's vacant-note
  // allocations for how these round-trip to the backend.
  const [vacantNotes, setVacantNotes] = useState<Record<string, string>>({});
  // Editable availability segments (inline editing within card)
  // Shown widened to every counter row the card holds (windowsCoveringRows), so a counter is never listed
  // outside the window it sits under — an older roster may hold a window narrower than its rows.
  const [editableSegs, setEditableSegs] = useState<Array<{ start: string; end: string }>>(() => {
    const segs = initialSegs();
    const rows = (initialAssignmentsRef.current ?? []).filter(hasCounter);
    return segs.length > 0 ? windowsCoveringRows(segs, rows, startHour) : segs;
  });
  function initialSegs(): Array<{ start: string; end: string }> {
    const meta = gridData.officer_metadata[officerId];
    const own = officerWindows(meta);
    if (own.length > 0) return own;
    // Fallback for SOS/OT: derive window from counter assignments so the
    // availability bar and "+ Add window" button always appear.
    // Derive officer type from ID prefix when metadata is missing (e.g. after
    // backend state restoration that strips the OT time suffix from the key).
    const officerType = meta?.officer_type
      ?? (officerId.startsWith('OT') ? 'OT'
        : officerId.startsWith('S') ? 'SOS'
        : 'MAIN');
    if (officerType === 'SOS' || officerType === 'OT') {
      const asgns = (initialAssignmentsRef.current ?? [])
        .filter(a => a.counter && a.counter !== '0' && a.startTime && a.endTime);
      if (asgns.length > 0) {
        return [{ start: asgns[0].startTime, end: asgns[asgns.length - 1].endTime }];
      }
      // No assignments yet — show an empty window card so the user must set times
      // before adding counters. Defaults to the full shift range as a starting point.
      return [{ start: timeSlots[0], end: timeSlots[timeSlots.length - 1] }];
    }
    return [];
  }
  const [savingAvail, setSavingAvail] = useState(false);
  const [showRegenBreakConfig, setShowRegenBreakConfig] = useState(false);
  const [regenBreakSizes, setRegenBreakSizes] = useState<(number | null)[]>([null, null, null]);
  const [regenBreakEarliestStart, setRegenBreakEarliestStart] = useState('');
  const [regenBreakLatestEnd, setRegenBreakLatestEnd] = useState('');
  const [regenShouldClear, setRegenShouldClear] = useState(false);
  const [showWhatsAppPicker, setShowWhatsAppPicker] = useState(false);
  const [pendingShareText, setPendingShareText] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactList | null>(null);
  const [footerText, saveFooter] = useWhatsAppFooter();
  const [footerDraft, setFooterDraft] = useState<string | null>(null);
  const [footerSaving, setFooterSaving] = useState(false);
  // The recipient once chosen — shown as a QR code rather than opened straight
  // away, so the user can pick which device actually sends it (scan with a
  // phone) instead of it always launching on whatever device the roster is
  // open on. `message` is editable right up until the QR is scanned/tapped —
  // the QR itself re-encodes live as the user edits it.
  const [pendingRecipient, setPendingRecipient] = useState<{ phone: string; label: string; pill?: RecipientPill } | null>(null);
  const [pendingMessage, setPendingMessage] = useState('');
  // Local undo stack
  const [undoStack, setUndoStack] = useState<AssignmentRow[][]>([]);
  const [expanded, setExpanded] = useState(false);

  // Original assignments — updated after each successful save so the next save
  // only diffs against the post-save state (not the original mount state).
  const [originalAssignments, setOriginalAssignments] = useState<AssignmentRow[]>(() => initialAssignmentsRef.current!);

  // ── Find highlighted row ─────────────────────────────────────────────────
  // Computed once at mount and stored stably so it doesn't shift as user edits rows.
  const [highlightedRowId] = useState<string | undefined>(() => {
    const dashIdx = clickedTimeLabel.indexOf('-');
    if (dashIdx < 0) return undefined;
    const clickedStart = clickedTimeLabel.slice(0, dashIdx);
    const clickedSlot = hmmmToSlot(clickedStart, startHour);
    for (const row of initialAssignmentsRef.current ?? []) {
      const rowStart = hmmmToSlot(row.startTime, startHour);
      const rowEnd = hmmmToEndSlot(row.endTime, startHour);
      if (clickedSlot >= rowStart && clickedSlot < rowEnd) {
        return row.id;
      }
    }
    return undefined;
  });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleMetadataChange = (
    _officerId: string,
    field: 'name' | 'deployment',
    value: string
  ) => {
    setCard((prev) => ({ ...prev, metadata: { ...prev.metadata, [field]: value } }));
    setIsDirty(true);
  };

  // ── Undo helpers ──────────────────────────────────────────────────────────
  const pushUndo = (assignments: AssignmentRow[]) => {
    setUndoStack(prev => [...prev.slice(-19), [...assignments]]);
  };

  // Every counter row must live inside an availability window. Widen (never shrink)
  // the window that already contains this row's OTHER, unedited endpoint — e.g.
  // editing endTime looks up the window containing startTime and pushes its end out
  // to match if the new endTime now runs past it. Deleting a row is untouched by
  // this: handleDeleteRow never calls this, so the window stays exactly as-is.
  const extendWindowForRow = (row: AssignmentRow, field: keyof AssignmentRow, value: string) => {
    if (field !== 'startTime' && field !== 'endTime') return;
    const anchorTime = field === 'endTime' ? row.startTime : row.endTime;
    let anchorSlot: number;
    try { anchorSlot = hmmmToSlot(anchorTime, startHour); } catch { return; }
    const segIdx = editableSegs.findIndex(seg => {
      try {
        const ss = hmmmToSlot(seg.start, startHour);
        const se = hmmmToEndSlot(seg.end, startHour);
        return anchorSlot >= ss && anchorSlot < se;
      } catch { return false; }
    });
    if (segIdx < 0) {
      // A row starting where every window has ended (the end it just got is its first): widen
      // the window it follows out to that end, and no further.
      if (field === 'endTime') {
        try {
          if (hmmmToSlot(row.startTime, startHour) < hmmmToEndSlot(value, startHour)) {
            updateSegs(widenWindowsToCover(editableSegs, row.startTime, value, startHour), !hasCounter(row));
          }
        } catch { /* bad time value — leave the windows alone */ }
      }
      return;
    }
    const seg = editableSegs[segIdx];
    try {
      if (field === 'endTime') {
        if (hmmmToEndSlot(value, startHour) > hmmmToEndSlot(seg.end, startHour)) {
          updateSegs(mergeAvailabilitySegments(
            editableSegs.map((s, i) => i === segIdx ? { ...s, end: value } : s), startHour,
          ), !hasCounter(row));
        }
      } else {
        if (hmmmToSlot(value, startHour) < hmmmToSlot(seg.start, startHour)) {
          updateSegs(mergeAvailabilitySegments(
            editableSegs.map((s, i) => i === segIdx ? { ...s, start: value } : s), startHour,
          ), !hasCounter(row));
        }
      }
    } catch { /* bad time value — leave the window alone */ }
  };

  const handleAssignmentChange = (
    _officerId: string,
    rId: string,
    field: keyof AssignmentRow,
    value: string
  ) => {
    pushUndo(card.assignments);
    const row = card.assignments.find(r => r.id === rId);
    setCard((prev) => ({
      ...prev,
      assignments: prev.assignments.map((r) => r.id === rId ? { ...r, [field]: value } : r),
    }));
    setIsDirty(true);
    setDirtyRowIds((prev) => new Set(prev).add(rId));
    if (row) extendWindowForRow(row, field, value);
    // Moving a counter's times: follow it along the timeline (the grid only moves if the new edge is out of view).
    const n = Number(row?.counter);
    if (row && !row.duty && n > 0 && value && (field === 'startTime' || field === 'endTime')) {
      try {
        const slot = field === 'startTime' ? hmmmToSlot(value, startHour) : hmmmToEndSlot(value, startHour) - 1;
        onRevealCounter?.(n, slot);
      } catch { /* unparsable time: stay put */ }
    }
  };

  // The counter dropdown also offers the free-text duties ("duty:SOS DCAR"): a duty row is counter "0"
  // with its text in `duty`; picking a counter takes the duty off. One undo step, one dirty mark.
  // Hovering or focusing a row of the card softly outlines its counter in the grid. Touch screens
  // have no hover (and emulate a stuck one), so there it is focus alone.
  const canHover = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;
  const pointAtRow = (row: AssignmentRow) => {
    const n = Number(row.counter);
    announceCounterFocus(!row.duty && n > 0 ? n - 1 : null);
  };
  useEffect(() => () => announceCounterFocus(null), []);

  // Counter dropdown colouring: is counter `n` free (or held, and by whom) for this row's start?
  const rowVacancy = (row: AssignmentRow, n: number): CounterVacancy | undefined => {
    let startSlot: number;
    try { startSlot = hmmmToSlot(row.startTime, startHour); } catch { return undefined; }
    return counterVacancyOnGrid(
      gridData.cells, gridData.num_slots, n, startSlot, officerId,
      (key) => gridData.officer_metadata[key]?.name || key,
    );
  };
  const rowEndSlot = (row: AssignmentRow): number | undefined => {
    if (!row.endTime) return undefined;
    try { return hmmmToEndSlot(row.endTime, startHour); } catch { return undefined; }
  };

  const handleChoiceChange = (rId: string, choice: string) => {
    pushUndo(card.assignments);
    const { duty } = parseCounterChoice(choice);
    const counter = duty ? '0' : choice;
    setCard((prev) => ({
      ...prev,
      assignments: prev.assignments.map((r) => r.id === rId ? { ...r, counter, duty } : r),
    }));
    setIsDirty(true);
    setDirtyRowIds((prev) => new Set(prev).add(rId));
    const counterNum = Number(counter);
    if (!duty && counterNum > 0) {
      const row = card.assignments.find(r => r.id === rId);
      let startSlot: number | null = null;
      try { if (row?.startTime) startSlot = hmmmToSlot(row.startTime, startHour); } catch { /* unparsable time: row only */ }
      onRevealCounter?.(counterNum, startSlot);
    }
  };

  // `afterRowId` inserts right after that row (used by the per-row "+ Add
  // counter" button) instead of always appending at the very end. When the
  // caller already knows the next row's start time (`overrideEnd` — the two
  // rows are adjacent), that's used verbatim instead of the window-derived
  // fallback, even if it collapses the new row to zero duration: that's
  // flagged visually (red border) rather than silently rewritten.
  // `fallbackStart` is the start used when there is no previous row to follow —
  // the owning window's start, so an empty window (e.g. 0230-1000) isn't widened
  // back out to the shift's first slot.
  const handleAddRow = (_officerId: string, afterRowId?: string, overrideEnd?: string, fallbackStart?: string) => {
    pushUndo(card.assignments);
    const newId = rowId();
    const afterIdx = afterRowId
      ? card.assignments.findIndex(r => r.id === afterRowId)
      : card.assignments.length - 1;
    const prevRow = afterIdx >= 0 ? card.assignments[afterIdx] : undefined;
    // Start comes from the previous row (see defaultNewRowRange).
    const { start: newStart, end: defaultEnd } = defaultNewRowRange({
      prevEnd: prevRow?.endTime, fallbackStart, overrideEnd, segs: editableSegs, timeSlots, startHour,
    });
    // The end is left blank for the user to choose — it used to default to the window's end, or
    // the shift's when the row started past every window, which widened the availability to
    // the end of the shift. The window follows the end the user picks (see extendWindowForRow).
    const newEnd = overrideEnd ? defaultEnd : '';

    const carriedNote = newEnd ? noteForNewRow(newStart, newEnd) : undefined;
    setCard((prev) => {
      const insertAt = afterRowId
        ? prev.assignments.findIndex(r => r.id === afterRowId) + 1
        : prev.assignments.length;
      const next = [...prev.assignments];
      next.splice(insertAt, 0, { id: newId, startTime: newStart, endTime: newEnd, counter: '', notes: carriedNote });
      return { ...prev, assignments: next };
    });
    setIsDirty(true);
    setDirtyRowIds((prev) => new Set(prev).add(newId));
  };

  const handleDeleteRow = (_officerId: string, rId: string) => {
    pushUndo(card.assignments);
    setCard((prev) => ({
      ...prev,
      assignments: prev.assignments.filter((r) => r.id !== rId),
    }));
    setIsDirty(true);
    setDirtyRowIds((prev) => { const next = new Set(prev); next.delete(rId); return next; });
  };

  const handleDeleteCard = (_officerId: string) => {
    setConfirmDelete(true);
  };

  const handleClearCounters = (_officerId: string) => {
    setConfirmClearCounters(true);
  };

  const handleUndoLocal = () => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const snapshot = prev[prev.length - 1];
      setCard(c => ({ ...c, assignments: snapshot }));
      setIsDirty(snapshot.length !== initialAssignmentsRef.current?.length ||
        snapshot.some((r, i) => {
          const o = initialAssignmentsRef.current![i];
          return !o || r.counter !== o.counter || r.duty !== o.duty || r.startTime !== o.startTime || r.endTime !== o.endTime || r.notes !== o.notes;
        }));
      setDirtyRowIds(new Set(snapshot.map(r => r.id)));
      return prev.slice(0, -1);
    });
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    // Clear this officer off BOTH grids (Counter and Officer tabs — see
    // optimisticRemoveOfficer, which patches the shared snapshot both read
    // from) right away, plus the popup's own row list via the same
    // onPreviewChange pipeline a live row-edit already uses. The removeOfficer
    // round trip is what onSaved/onClose wait on below, but there's no reason
    // the GRID has to wait on it too.
    const rollback = onOptimisticRemove?.(officerId);
    setCard((prev) => ({ ...prev, assignments: [] }));
    try {
      // Fully removes the officer (and any remaining assignments) from the roster —
      // distinct from handleConfirmClearCounters, which only empties their counters.
      const result = await apiClient.removeOfficer({ officer: officerId });
      notifySuccess(`Removed ${officerId}`);
      skipUnmountSaveRef.current = true;
      onSaved(result);
      onClose();
    } catch (err) {
      notifyError(`Failed to remove officer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setDeleting(false);
      setConfirmDelete(false);
      // The delete never happened — put the preview back the way it was.
      rollback?.();
      setCard((prev) => ({ ...prev, assignments: originalAssignments }));
    }
  };

  const handleConfirmClearCounters = async () => {
    const assignments = card.assignments;
    if (assignments.length === 0) { setConfirmClearCounters(false); return; }
    setClearingCounters(true);
    // Same instant-preview treatment as handleConfirmDelete — clear the grid now,
    // reconcile with the real response (or roll back) once it lands.
    const rollback = onOptimisticClearCounters?.(officerId);
    setCard((prev) => ({ ...prev, assignments: [] }));
    try {
      // Delete all assignments: use start of first and end of last to cover the full range.
      // The officer card itself stays — this is intentionally NOT the same as removeOfficer,
      // since a blank/zero-counter officer is a valid state (manual pen-and-paper workflow).
      const startTime = assignments[0].startTime;
      const endTime = assignments[assignments.length - 1].endTime;
      const result = await apiClient.deleteOfficer({ officer: officerId, start_time: startTime, end_time: endTime });
      notifySuccess(`Cleared counters for ${card.metadata.name || officerId}`);
      setOriginalAssignments([]);
      setIsDirty(false);
      setDirtyRowIds(new Set());
      setUndoStack([]);
      onSaved(result);
      setConfirmClearCounters(false);
    } catch (err) {
      notifyError(`Failed to clear counters: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // The clear never happened — put the preview back the way it was.
      rollback?.();
      setCard((prev) => ({ ...prev, assignments }));
    } finally {
      setClearingCounters(false);
    }
  };

  // ── Change trail ─────────────────────────────────────────────────────────
  // Compare original vs current assignments and produce human-readable lines.
  function buildChangeTrail(
    original: AssignmentRow[],
    current: AssignmentRow[],
    displayName: string,
  ): string {
    const lines: string[] = [];
    const label = (row: AssignmentRow) =>
      row.duty ?? formatCounterLabel(parseInt(row.counter, 10), operation_mode);

    for (const cur of current) {
      if (!hasCounter(cur)) continue;
      const orig = original.find((r) => r.id === cur.id);
      if (!orig) {
        // New row added
        lines.push(
          `${displayName}: added ${label(cur)} ${cur.startTime}–${cur.endTime}`
        );
      } else if (
        orig.counter !== cur.counter ||
        orig.duty !== cur.duty ||
        orig.startTime !== cur.startTime ||
        orig.endTime !== cur.endTime
      ) {
        // Row changed — show what changed
        if (orig.counter !== cur.counter || orig.duty !== cur.duty) {
          lines.push(
            `${displayName}: changed from ${label(orig)} to ${label(cur)} ${cur.startTime}–${cur.endTime}`
          );
        } else {
          // Same counter, different time
          lines.push(
            `${displayName}: ${label(cur)} ${orig.startTime}–${orig.endTime} → ${cur.startTime}–${cur.endTime}`
          );
        }
      }
    }
    for (const orig of original) {
      if (!current.find((r) => r.id === orig.id)) {
        lines.push(
          `${displayName}: removed from ${label(orig)} ${orig.startTime}–${orig.endTime}`
        );
      }
    }
    return lines.join('\n');
  }

  // Back-to-back rows on the same counter (e.g. 1945-2000 and 2000-2200 on DC12) read as
  // one stretch in a message — the officer doesn't move, so it is listed once.
  const mergeSameCounter = <R extends { counter: string; duty?: string; startTime: string; endTime: string }>(rows: R[]): R[] => {
    const out: R[] = [];
    for (const row of rows) {
      const prev = out[out.length - 1];
      if (prev && prev.counter === row.counter && prev.duty === row.duty && prev.endTime === row.startTime) {
        out[out.length - 1] = { ...prev, endTime: row.endTime };
      } else {
        out.push(row);
      }
    }
    return out;
  };

  // The time after a counter row, as the schedule text says it: a free-text duty the officer is on
  // ("SOS DCAR", "B@M CTRL BOOTH" — RosterGridData.duty_slots) by name, the rest of a gap between two
  // rows as BREAK. `to` null is the end of the shift, where only a duty is worth a line.
  const officerDuties = gridData.duty_slots?.[officerId];
  const gapLines = (from: string, to: string | null): string[] => scheduleGapLines(from, to, startHour, officerDuties);
  // A counter line, marked "(no relief)" where nobody takes the counter over — the same points the No Relief
  // pop-up and the grid's NR buttons show (NoReliefContext).
  const { ends: noReliefEnds } = useNoReliefView();
  const counterLine = (row: AssignmentRow) =>
    scheduleCounterLine(row, formatCounterLabel(parseInt(row.counter, 10), operation_mode), startHour, noReliefEnds);

  // ── Schedule text builder — segment-aware ────────────────────────────────
  const buildScheduleText = (segFilter?: Array<{ start: string; end: string }>) => {
    // The windows as the card shows them now (not the copy taken when it opened), widened to every row on it:
    // a counter the card lists is always in the message, whichever window it ran past.
    const counterRows = card.assignments.filter((r) => hasCounter(r) && !r.duty);
    const covered = windowsCoveringRows(editableSegs, counterRows, startHour);
    const allSegs = covered.length > 0 ? covered : null;

    const segsToUse = segFilter ? windowsCoveringRows(segFilter, [], startHour) : allSegs;

    if (!segsToUse) {
      // No segment info — flat list with breaks
      const lines: string[] = [];
      const rows = mergeSameCounter(card.assignments.filter((r) => hasCounter(r) && !r.duty));
      rows.forEach((row, i) => {
        lines.push(counterLine(row));
        const next = rows[i + 1];
        if (next) lines.push(...gapLines(row.endTime, next.startTime));
        else lines.push(...gapLines(row.endTime, null));
      });
      return lines.join('\n');
    }

    // Group assignments by segment; show blank line between segments
    const allLines: string[] = [];
    for (const seg of segsToUse) {
      const segRows = mergeSameCounter(rowsInWindow(counterRows, seg, startHour));
      if (segRows.length === 0) continue;
      if (allLines.length > 0) allLines.push('');
      segRows.forEach((row, i) => {
        allLines.push(counterLine(row));
        const next = segRows[i + 1];
        if (next) allLines.push(...gapLines(row.endTime, next.startTime));
        else allLines.push(...gapLines(row.endTime, seg.end));
      });
    }
    return allLines.join('\n');
  };

  // ── Copy schedule ─────────────────────────────────────────────────────────
  const handleCopySchedule = (text?: string, segIdx?: number) => {
    const str = text ?? buildScheduleText();
    navigator.clipboard.writeText(str).then(() => {
      if (segIdx !== undefined) {
        setCopiedSegIdx(segIdx);
        setTimeout(() => setCopiedSegIdx(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  const handleOpenWhatsAppPicker = async (shareText?: string) => {
    setPendingShareText(shareText ?? null);
    setShowWhatsAppPicker(true);
    if (contacts === null) {
      try {
        const result = await apiClient.getContacts();
        setContacts(result);
      } catch {
        setContacts({ teams: [], contacts: [], updated_at: null, updated_by: null, filename: null });
      }
    }
  };

  const normaliseSgPhone = (raw: string): string => {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 8 && /^[689]/.test(digits)) digits = '65' + digits;
    return digits;
  };

  const handleSendWhatsAppByPhone = (rawDigits: string, greeting = 'Hello', recipientLabel?: string, recipientPill?: RecipientPill) => {
    const digits = normaliseSgPhone(rawDigits);
    if (!digits) {
      notifyError('No phone number available for this contact');
      return;
    }
    // Shown as a QR code instead of opened here — the person composing this
    // isn't necessarily the one who should send it. Actually opening the link
    // happens on a click of the QR itself (see the QR step below), or by
    // scanning it with a phone. The message stays editable right up to that
    // point — the QR re-encodes live as WhatsAppQrStep's textarea changes.
    // The session's footer closes the message; editing it there only changes
    // this one message (the session-wide footer is edited in the picker).
    const footer = footerText.trim();
    setPendingMessage(`${greeting}\n\n${pendingShareText ?? buildScheduleText()}${footer ? `\n\n${footer}` : ''}`);
    setPendingRecipient({ phone: digits, label: recipientLabel ?? rawDigits, pill: recipientPill });
  };

  const closeWhatsAppFlow = () => {
    setShowWhatsAppPicker(false);
    setPendingShareText(null);
    setFooterDraft(null);
    setPendingRecipient(null);
    setPendingMessage('');
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const hasInvalidRows = card.assignments.some(
    (r) => !isValidTimeRange(r.startTime, r.endTime, shiftType)
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  //
  // The write is queued, not awaited: it goes out the instant the edit is made,
  // survives this panel being torn down, and is retried until it lands (see
  // web/src/utils/saveQueue.ts). Callers that genuinely need the server up to
  // date before their next step await flushSaves(saveKey) instead.
  // One key per kind of write, because the queue keeps only the newest task per
  // key: an availability change and a counter change carry different state, so
  // sharing a key would let one silently replace the other.
  const saveKey = `officer:${officerId}:assignments`;
  const availabilitySaveKey = `officer:${officerId}:availability`;
  /** The payload of the last write queued, so an unchanged state is never sent
   *  twice — a duplicate write is a duplicate version in the roster's history. */
  const lastQueuedRef = useRef<string | null>(null);
  const savedSegsRef = useRef(editableSegs);
  const newsRef = useRef(false);
  const availNewsRef = useRef(false);
  const savedMetaRef = useRef(`${card.metadata.name ?? ''}|${card.metadata.deployment ?? ''}`);

  const handleSave = (): boolean => {
    if (!isDirty) return true;
    if (hasInvalidRows) {
      notifyError('Fix invalid time ranges (end must be after start) before saving');
      return false;
    }
    const displayName = card.metadata.name || officerId;
    const trail = buildChangeTrail(originalAssignments, card.assignments, displayName);

    // Build delete ranges from removed / time-shifted original rows
    const deletedRows = originalAssignments.filter(
      (orig) => !card.assignments.find((cur) => cur.id === orig.id),
    );
    const modifiedRows = originalAssignments.filter((orig) => {
      const cur = card.assignments.find((r) => r.id === orig.id);
      // A note-only change (counter/time unchanged) still counts as "modified" so
      // it goes through the normal delete+reallocate save path below, same as any
      // other edit — a note is part of the assignment, not a side channel.
      return cur && (
        cur.startTime !== orig.startTime ||
        cur.endTime !== orig.endTime ||
        cur.counter !== orig.counter ||
        cur.duty !== orig.duty ||
        (cur.notes ?? '') !== (orig.notes ?? '')
      );
    });
    const toDelete = [...deletedRows, ...modifiedRows].map((r) => ({
      start: r.startTime,
      end: r.endTime,
    }));

    const allocations: ManualAllocation[] = card.assignments.filter(hasCounter).map((row) => ({
      officer: officerId,
      type: card.metadata.officer_type,
      counter: row.duty ? 0 : parseInt(row.counter),
      ...(row.duty ? { duty: row.duty } : {}),
      start: row.startTime,
      end: row.endTime,
      duration: calcDurationMinutes(row.startTime, row.endTime, startHour),
      deployment: card.metadata.deployment,
      note: row.notes,
    }));
    allocations.push(...vacantAllocations());

    // Single atomic API call — creates one history entry instead of delete + allocate
    const request = {
      officer: officerId,
      to_delete: toDelete,
      allocations,
      metadata: { [officerId]: { name: card.metadata.name, deployment: card.metadata.deployment ?? '' } },
      description: trail || `${officerId} updated`,
    };
    const signature = JSON.stringify(request);
    if (signature === lastQueuedRef.current) return true;
    // Only a change to what the server actually holds is worth a "Saved" tick: a counter row with no
    // counter yet (just added, or still being filled in) is not sent, so it saves nothing.
    const metaKey = `${card.metadata.name ?? ''}|${card.metadata.deployment ?? ''}`;
    // Remembered until a write lands: the queue keeps only the newest write per key, so the one
    // that carried the real change may be replaced by a later no-news write.
    if (rowsChangedRoster(originalAssignments, card.assignments) || metaKey !== savedMetaRef.current) {
      newsRef.current = true;
    }
    savedMetaRef.current = metaKey;
    lastQueuedRef.current = signature;

    // The baseline moves now rather than on the response: this write is the
    // state the server will hold once the queue drains, so the next edit must
    // diff against it — waiting would make a second edit delete the wrong rows.
    setOriginalAssignments([...card.assignments]);
    setIsDirty(false);
    setDirtyRowIds(new Set());
    setUndoStack([]);

    queueSave(saveKey, async () => {
      setSaving(true);
      try {
        const result = await apiClient.updateOfficerAssignments(request);
        // A failed write surfaces via the save queue's global failure handler; a landed one
        // gets a short tick so nobody has to close the popup to learn it was saved.
        onSavedRef.current(result);  // canvas repaints in-place — no scroll jump
        if (newsRef.current) { newsRef.current = false; notifySuccess(SAVED_PREFIX); }
      } finally {
        setSaving(false);
      }
    });
    return true;
  };

  // Sync unsaved popup edits to the backend without closing.
  // Called before auto-assign so the backend sees any pending deletions/modifications.
  const applySaveToBackendWithoutClosing = async (): Promise<boolean> => {
    // Queued edits are already on their way; auto-assign reads the roster back,
    // so it has to wait for them to land before it plans anything.
    await flushSaves(saveKey);
    if (!isDirty) return true;
    if (hasInvalidRows) {
      notifyError('Fix invalid time ranges before auto-assigning');
      return false;
    }
    try {
      const deletedRows = originalAssignments.filter(
        (orig) => !card.assignments.find((cur) => cur.id === orig.id),
      );
      const modifiedRows = originalAssignments.filter((orig) => {
        const cur = card.assignments.find((r) => r.id === orig.id);
        // Note-only edits count as "modified" too — otherwise they'd never make
        // it into toAllocate below and the note would silently fail to save.
        return cur && (
          cur.startTime !== orig.startTime ||
          cur.endTime !== orig.endTime ||
          cur.counter !== orig.counter ||
          cur.duty !== orig.duty ||
          (cur.notes ?? '') !== (orig.notes ?? '')
        );
      });
      const toDelete = [...deletedRows, ...modifiedRows].map((r) => ({ start: r.startTime, end: r.endTime }));

      const modifiedCurrent = modifiedRows
        .map(orig => card.assignments.find(r => r.id === orig.id))
        .filter((r): r is AssignmentRow => r !== undefined);
      const newRows = card.assignments.filter(
        cur => !originalAssignments.find(orig => orig.id === cur.id),
      );
      const toAllocate = [...modifiedCurrent, ...newRows].filter(hasCounter);
      const allocations: ManualAllocation[] = toAllocate.map((row) => ({
        officer: officerId,
        type: card.metadata.officer_type,
        counter: row.duty ? 0 : parseInt(row.counter),
        ...(row.duty ? { duty: row.duty } : {}),
        start: row.startTime,
        end: row.endTime,
        duration: calcDurationMinutes(row.startTime, row.endTime, startHour),
        deployment: card.metadata.deployment,
        note: row.notes,
      }));

      await apiClient.updateOfficerAssignments({
        officer: officerId,
        to_delete: toDelete,
        allocations,
        description: `${officerId} pre-sync before auto-assign`,
      });

      // Update baseline so the subsequent regen sees clean state
      setOriginalAssignments([...card.assignments]);
      setIsDirty(false);
      setDirtyRowIds(new Set());
      setUndoStack([]);
      return true;
    } catch (err) {
      notifyError(`Failed to apply changes: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return false;
    }
  };

  const myOverrides = {
    cleared: breakOverrides?.cleared?.[officerId],
    userBreaks: breakOverrides?.userBreaks?.[officerId],
  };

  const handleAutoAssign = async () => {
    // Sync unsaved edits first, then show the break-config panel.
    setRegenerating(true);
    try {
      if (isDirty) {
        const synced = await applySaveToBackendWithoutClosing();
        if (!synced) { setRegenerating(false); return; }
      }
    } catch {
      setRegenerating(false);
      return;
    }
    setRegenerating(false);
    const isOT = card.metadata.officer_type === 'OT';
    if (isOT && editableSegs.length > 0) {
      const first = editableSegs[0];
      const last  = editableSegs[editableSegs.length - 1];
      setRegenBreakSizes(computeOTBreakSizes(first.start, last.end, shiftType));
      const win = computeOTBreakWindowDefaults(first.start, last.end, shiftType);
      setRegenBreakEarliestStart(win.earliest);
      setRegenBreakLatestEnd(win.latestEnd);
    } else {
      setRegenBreakSizes(computeDefaultBreakSizes(editableSegs, shiftType));
      setRegenBreakEarliestStart('');
      setRegenBreakLatestEnd('');
    }
    // Fill just the green slots by default; only when there are none does the panel start
    // in clear-and-reassign mode (the user can also opt into it with the panel's checkbox).
    const curRows = card.assignments.filter(r => r.counter !== '' && r.counter !== '0');
    const vacant = computeVacantRanges(editableSegs, curRows, startHour, timeSlots, myOverrides);
    setRegenShouldClear(vacant.length === 0);
    setShowRegenBreakConfig(true);
  };

  const handleConfirmRegen = async () => {
    setShowRegenBreakConfig(false);
    setRegenerating(true);
    const customBreaks = regenBreakSizes.filter((s): s is number => s !== null);
    try {
      const response = await apiClient.regenerateOfficer({
        officer_key: officerId,
        // Cleared server-side in the same edit as the fill: one version to undo.
        clear_ranges: regenShouldClear
          ? editableSegs.map(seg => ({ start_time: seg.start, end_time: seg.end }))
          : undefined,
        custom_break_sizes: customBreaks.length > 0 ? customBreaks : undefined,
        break_earliest: regenBreakEarliestStart || undefined,
        break_latest_end: regenBreakLatestEnd || undefined,
        // After a clear there are no overrides worth honouring for the cleared span.
        cleared_break_slots: !regenShouldClear && myOverrides.cleared?.length ? myOverrides.cleared : undefined,
        user_break_slots: !regenShouldClear && myOverrides.userBreaks?.length ? myOverrides.userBreaks : undefined,
      });
      notifySuccess(response.message);
      skipUnmountSaveRef.current = true;
      onSaved(response);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (!regenShouldClear && (msg.includes('No unassigned time') || msg.includes('Delete counter'))) {
        // No vacant slots — escalate to clear-and-reassign within the same panel
        setRegenShouldClear(true);
        setShowRegenBreakConfig(true);
      } else {
        notifyError(`Auto-assign failed: ${msg}`);
      }
      setRegenerating(false);
    }
  };

  // Restore (main officers only — they alone have a fixed schedule): back on the roster they were built from, the
  // counters the uploaded roster prints for them, for the whole shift. Not what ⚡ Auto does, which plans counters and
  // breaks afresh against the board.
  const handleRestoreFull = async () => {
    setRegenerating(true);
    try {
      if (isDirty && !(await applySaveToBackendWithoutClosing())) return;
      const result = await apiClient.restoreOfficer({ officer_key: officerId });
      notifySuccess(result.message);
      skipUnmountSaveRef.current = true;
      onSaved(result);
      onClose();
    } catch (err) {
      notifyError(`Restore failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRegenerating(false);
    }
  };

  // Availability windows save on change, through the same queue as the counter
  // rows: picking a time on a mobile scroll-wheel fires several changes in a
  // row, and the queue collapses them to the last one instead of a timer doing
  // it — which also means the last one is never the one still waiting on a
  // timer when the panel closes.
  const saveAvailability = useCallback((segs: Array<{ start: string; end: string }>, silent = false) => {
    if (segs.length === 0) return;
    // Earliest start to latest end on the shift's clock, not the first and last
    // window as listed: edit a night officer's first window to a morning time
    // and by position the bracket reads 0900 to 2300 — backwards.
    const span = timingSpan(segs.map(s => `${s.start}-${s.end}`).join(','), startHour);
    if (!span) return;
    // A write that leaves the windows as they were (e.g. a row added inside one) is no news.
    // `silent`: the window was only stretched to hold a row that has no counter yet — the user
    // didn't ask to change availability, so nothing is announced.
    if (availabilityNews(savedSegsRef.current, segs, silent)) availNewsRef.current = true;
    savedSegsRef.current = segs;
    queueSave(availabilitySaveKey, async () => {
      setSavingAvail(true);
      try {
        const result = await apiClient.updateAvailability({
          officer_key: officerId,
          availability_start: span.start,
          availability_end: span.end,
          segments: segs.length > 1 ? segs : undefined,
        });
        onSavedRef.current(result);
        if (availNewsRef.current) { availNewsRef.current = false; notifySuccess(SAVED_PREFIX); }
      } finally {
        setSavingAvail(false);
      }
    });
  }, [officerId, availabilitySaveKey, startHour, notifySuccess]);

  // Convenience: update segs, save, and notify parent for instant canvas preview.
  const updateSegs = (newSegs: Array<{ start: string; end: string }>, silent = false) => {
    setEditableSegs(newSegs);
    saveAvailability(newSegs, silent);
    onAvailabilityChange?.(newSegs);
  };

  // Deleting an availability window whose range still holds counter rows would
  // orphan them outside any window — a counter must always live inside one. If
  // the window is empty, delete it outright (previous behavior); otherwise ask
  // first, since this takes those counter assignments with it.
  const deleteAvailWindow = (si: number) => {
    const seg = editableSegs[si];
    if (!seg) return;
    const affectedRows = rowsInWindow(card.assignments, seg, startHour);
    if (affectedRows.length === 0) {
      updateSegs(editableSegs.filter((_, i) => i !== si));
      return;
    }
    setWindowDeleteConfirm({ segIndex: si, affectedRows });
  };

  const handleConfirmDeleteAvailWindow = () => {
    if (!windowDeleteConfirm) return;
    const { segIndex, affectedRows } = windowDeleteConfirm;
    pushUndo(card.assignments);
    const removeIds = new Set(affectedRows.map(r => r.id));
    setCard(prev => ({ ...prev, assignments: prev.assignments.filter(r => !removeIds.has(r.id)) }));
    setDirtyRowIds(prev => {
      const next = new Set(prev);
      removeIds.forEach(id => next.delete(id));
      return next;
    });
    setIsDirty(true);
    updateSegs(editableSegs.filter((_, i) => i !== segIndex));
    setWindowDeleteConfirm(null);
  };

  // Close handler — auto-saves any pending valid changes before closing
  const handleRequestCloseRef = useRef<() => void>(() => {});
  handleRequestCloseRef.current = () => {
    // A new counter row is added with its end blank; leaving without one would drop the row
    // (and its availability) unsaved, so the user has to pick an end or remove the row first.
    if (card.assignments.some(r => !r.endTime)) {
      notifyError('Select an end time for the new counter row (or remove it) before closing');
      return;
    }
    // Mark that we're handling close ourselves — unmount cleanup should skip its save
    skipUnmountSaveRef.current = true;
    // Queue anything not yet sent, then close: the write finishes on its own,
    // so closing never has to wait on the network.
    if (isDirty && !hasInvalidRows) handleSaveRef.current();
    onClose();
  };
  const handleRequestClose = () => handleRequestCloseRef.current();

  // Refs so the single keyboard listener always sees the latest save state
  const handleSaveRef = useRef<() => boolean>(() => false);
  handleSaveRef.current = handleSave;
  const canSaveRef = useRef(false);
  canSaveRef.current = isDirty && !saving && !hasInvalidRows;
  const handleUndoLocalRef = useRef<() => void>(() => {});
  handleUndoLocalRef.current = handleUndoLocal;

  // Tracking refs for unmount-save — updated every render so the cleanup closure reads latest values
  const skipUnmountSaveRef = useRef(false);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const cardRef = useRef(card);
  cardRef.current = card;
  const originalAssignmentsRef2 = useRef(originalAssignments);
  originalAssignmentsRef2.current = originalAssignments;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const hasInvalidRowsRef = useRef(hasInvalidRows);
  hasInvalidRowsRef.current = hasInvalidRows;

  // Escape closes; Ctrl+Z undos; Ctrl+S saves (uses refs so closure is never stale)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleRequestCloseRef.current(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleUndoLocalRef.current();
        return;
      }
      // The popup has no redo of its own — swallow it so it doesn't redo the whole roster behind the panel.
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (canSaveRef.current) handleSaveRef.current();
      }
    };
    // Capture phase, so this runs before the page-level roster undo/redo and can pre-empt it.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    if (asCard) return;
    const cls = fromBottom ? 'edit-panel-open-bottom' : 'edit-panel-open';
    document.body.classList.add(cls);
    return () => { document.body.classList.remove(cls); };
  }, [asCard, fromBottom]);

  // Counter/time/counter-row changes (picked, not typed) save the moment they
  // change — no debounce. A delay here is exactly how an edit gets lost: pick a
  // counter, switch pane a moment later, and the panel unmounts with the change
  // still only in React state. The write itself is queued (see handleSave), so
  // a slow connection costs the user nothing and the panel never has to stay
  // open for it. Deliberately excludes `notes` (see below) — a signature that
  // reacted to every keystroke in the notes field fired one full delete +
  // reallocate round trip (and one history version) PER CHARACTER, which is
  // what made typing a note feel like it hung the app.
  //
  // Both save effects below read isDirty/hasInvalidRows through refs rather than
  // depending on them. Depending on them is what defeated the debounce: every
  // keystroke flips isDirty false→true (the previous save cleared it), which
  // re-ran THIS effect — and this one saves with no delay. `hasInvalidRows` is
  // derived purely from the row times, so it can only change when the structural
  // signature does; nothing is missed by leaving it out of the deps.
  const structuralSignature = JSON.stringify(
    card.assignments.map((r) => [r.id, r.startTime, r.endTime, r.counter, r.duty ?? '']),
  );
  useEffect(() => {
    if (!isDirtyRef.current || hasInvalidRowsRef.current) return;
    handleSaveRef.current();
  }, [structuralSignature]);

  // Name, deployment, and per-row notes are all typed rather than picked, so
  // they settle for a moment before being sent — one write per keystroke is
  // noise, not safety. Nothing is at risk in the meantime: the unmount flush
  // below catches a half-typed value, and everything rides along with the next
  // assignment write regardless of which field triggered it (handleSave always
  // sends the CURRENT full row state, not just the field that changed).
  const assignmentsSignature = JSON.stringify(card.assignments);
  const debouncedSignature = `${card.metadata.name}\u0000${card.metadata.deployment ?? ''}\u0000${assignmentsSignature}\u0000${JSON.stringify(vacantNotes)}`;
  useEffect(() => {
    if (!isDirtyRef.current || hasInvalidRowsRef.current) return;
    const timer = setTimeout(() => {
      if (!isDirtyRef.current || hasInvalidRowsRef.current) return;
      handleSaveRef.current();
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedSignature]);

  // Unmount flush: the panel can be torn down without going through
  // handleRequestClose (a canvas click via onInteraction → setEditedOfficer(null),
  // or the pane itself unmounting on a tab switch), and a change made in the
  // same tick as that teardown may not have reached the save effect. Queue it
  // here through the same path, so it is written — and retried — exactly like
  // any other edit rather than as a best-effort shot that nobody watches.
  useEffect(() => {
    return () => {
      if (skipUnmountSaveRef.current) return;
      if (!isDirtyRef.current || hasInvalidRowsRef.current) return;
      handleSaveRef.current();
    };
  }, []);

  // Notify parent of assignment changes immediately so the counter canvas can preview
  // without waiting for save. Vacant-range notes ride along too — otherwise the very
  // act of opening this popup would mask any of this officer's already-saved vacant
  // notes out of the parent's merged view (see effectiveAssignmentNotes in HomeTab),
  // since that live overlay used to only ever know about `assignments`.
  //
  // The parent turns this into a full roster-grid rebuild (HomeTab's gridData/
  // effectiveGridData memos, plus OfficerScheduleGrid's own per-officer cell pass),
  // which is cheap once but not once-per-character. A picked field (start/end/counter,
  // add/remove row, avail window edit) changes structuralSignature/editableSegs and
  // fires immediately — that's the whole point of a live preview for those. A TYPED
  // field (a row note or vacant-range note) doesn't touch either, so it's debounced:
  // otherwise typing a note rebuilt the entire grid on every keystroke, which is what
  // made typing feel like it hung the app.
  const onPreviewChangeRef = useRef(onPreviewChange);
  onPreviewChangeRef.current = onPreviewChange;
  const previewStructuralSignature = `${structuralSignature}\u0000${JSON.stringify(editableSegs)}`;
  const lastPreviewStructuralSigRef = useRef(previewStructuralSignature);
  useEffect(() => {
    const fire = () => {
      const notedVacantRanges = allVacantRanges()
        .map(r => ({ ...r, note: vacantNoteFor(r) }))
        .filter(r => r.note);
      // A row still waiting for its end time has no extent to draw yet.
      onPreviewChangeRef.current?.(card.assignments.filter(a => a.endTime), notedVacantRanges);
    };
    const structuralChanged = previewStructuralSignature !== lastPreviewStructuralSigRef.current;
    lastPreviewStructuralSigRef.current = previewStructuralSignature;
    if (structuralChanged) {
      fire();
      return;
    }
    const timer = setTimeout(fire, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.assignments, editableSegs, vacantNotes, previewStructuralSignature]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function getVacantRanges(
    seg: { start: string; end: string },
    segAssignments: AssignmentRow[],
  ): Array<{ start: string; end: string }> {
    if (segAssignments.length === 0) return [{ start: seg.start, end: seg.end }];
    const sorted = [...segAssignments].sort((a, b) => hmmmToSlot(a.startTime, startHour) - hmmmToSlot(b.startTime, startHour));
    const ss = hmmmToSlot(seg.start, startHour);
    const se = hmmmToEndSlot(seg.end, startHour);
    const firstWork = hmmmToSlot(sorted[0].startTime, startHour);
    const lastWorkEnd = hmmmToEndSlot(sorted[sorted.length - 1].endTime, startHour);
    const vacant: Array<{ start: string; end: string }> = [];
    if (firstWork > ss) vacant.push({ start: seg.start, end: sorted[0].startTime });
    if (lastWorkEnd < se) vacant.push({ start: sorted[sorted.length - 1].endTime, end: seg.end });
    return vacant;
  }

  const vacantNoteKey = (range: { start: string; end: string }) => `${range.start}-${range.end}`;

  // Whatever's already persisted for this vacant range — the first non-empty note
  // among its slots, mirroring how a counter row's note is resolved elsewhere.
  function savedVacantNote(range: { start: string; end: string }): string {
    const notes = gridData.assignment_notes?.[officerId];
    if (!notes) return '';
    try {
      const s = hmmmToSlot(range.start, startHour);
      const e = hmmmToEndSlot(range.end, startHour);
      for (let slot = s; slot < e; slot++) {
        const n = notes[String(slot)];
        if (n) return n;
      }
    } catch { /* bad range — no note */ }
    return '';
  }

  // Local edit wins over the saved value once the user's typed anything for this range.
  function vacantNoteFor(range: { start: string; end: string }): string {
    const key = vacantNoteKey(range);
    return key in vacantNotes ? vacantNotes[key] : savedVacantNote(range);
  }

  // Every vacant range currently shown, across however many availability windows
  // there are — mirrors the multi-seg/single-seg/no-seg branching in innerBody so
  // handleSave sees exactly what the table renders.
  function allVacantRanges(): Array<{ start: string; end: string }> {
    if (editableSegs.length > 1) {
      return editableSegs.flatMap(eSeg => getVacantRanges(eSeg, rowsInWindow(card.assignments, eSeg, startHour)));
    }
    if (editableSegs.length === 1) return getVacantRanges(editableSegs[0], card.assignments);
    return [];
  }

  // A new counter row can land squarely on a vacant range that already carries a
  // note (e.g. "+ Add counter" filling a noted gap). The note is tagged to that
  // stretch of time, not to it being vacant, so it should ride along onto the row
  // rather than silently vanish once the range stops being vacant.
  function noteForNewRow(start: string, end: string): string | undefined {
    try {
      const newStart = hmmmToSlot(start, startHour);
      const newEnd = hmmmToEndSlot(end, startHour);
      for (const range of allVacantRanges()) {
        const rangeStart = hmmmToSlot(range.start, startHour);
        const rangeEnd = hmmmToEndSlot(range.end, startHour);
        if (newStart < rangeEnd && newEnd > rangeStart) {
          const note = vacantNoteFor(range);
          if (note) return note;
        }
      }
    } catch { /* bad time value — no note to carry over */ }
    return undefined;
  }

  // Vacant ranges never carry a counter, but a note on one still needs to reach the
  // backend — sent as a counter:0 "allocation" (a no-op schedule write; only the
  // note is real, see ManualAllocation.counter). Resent in full on every save so a
  // range whose note was cleared, or whose boundary shifted, doesn't leave a stale
  // note stuck on slots no vacant range covers anymore.
  function vacantAllocations(): ManualAllocation[] {
    return allVacantRanges().map(range => ({
      officer: officerId,
      type: card.metadata.officer_type,
      counter: 0,
      start: range.start,
      end: range.end,
      duration: calcDurationMinutes(range.start, range.end, startHour),
      deployment: card.metadata.deployment,
      note: vacantNoteFor(range),
    }));
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Deleting an availability window that still has counter rows inside it —
  // confirm before taking those assignments with it (see deleteAvailWindow).
  if (windowDeleteConfirm) {
    const { affectedRows } = windowDeleteConfirm;
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: '22px 26px', maxWidth: 380, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>This will delete counter assignments</div>
          <div style={{ fontSize: 13, color: '#000', marginBottom: 12 }}>
            Deleting this availability window removes the following counter assignment{affectedRows.length > 1 ? 's' : ''}:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18, maxHeight: 220, overflowY: 'auto' }}>
            {affectedRows.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>
                <span style={{ fontWeight: 700, color: '#991b1b' }}>{r.duty ?? formatCounterLabel(parseInt(r.counter, 10), operation_mode)}</span>
                <span style={{ fontFamily: 'monospace', color: '#000' }}>{fmtHHMM(r.startTime)}–{fmtHHMM(r.endTime)}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleConfirmDeleteAvailWindow}
              style={{ flex: 1, padding: '9px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >Delete window &amp; counters</button>
            <button
              onClick={() => setWindowDeleteConfirm(null)}
              style={{ flex: 1, padding: '9px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
            >Cancel</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Break config + officer summary before clear-and-reassign
  if (showRegenBreakConfig) {
    const meta = card.metadata;
    const isSOS = meta.officer_type === 'SOS';
    const isOT  = meta.officer_type === 'OT';
    const showBreaks = isSOS || isOT;
    const existingRows = card.assignments.filter(r => r.counter !== '' && r.counter !== '0');
    const counterLabel = (num: string) => gridData.counter_labels[parseInt(num) - 1] ?? num;
    const availSegs = editableSegs.length > 0
      ? editableSegs
      : (meta.availability_start && meta.availability_end ? [{ start: meta.availability_start, end: meta.availability_end }] : []);
    const vacantRanges = computeVacantRanges(availSegs, existingRows, startHour, timeSlots, myOverrides);
    const sosColor = { bg: '#fce7f3', text: '#9d174d' };
    const otColor  = { bg: '#ffedd5', text: '#9a3412' };
    const badgeColor = isOT ? otColor : sosColor;

    const durLabel = (startTime: string, endTime: string) => {
      const mins = calcDurationMinutes(startTime, endTime, startHour);
      return mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}` : `${mins}m`;
    };

    const SummaryRow = ({ counter, startTime, endTime, color }: { counter: string; startTime: string; endTime: string; color?: { bg: string; text: string } }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ background: (color ?? badgeColor).bg, color: (color ?? badgeColor).text, fontWeight: 700, fontSize: 12, padding: '2px 7px', borderRadius: 5, minWidth: 40, textAlign: 'center' }}>
          {counter}
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#000', fontWeight: 500 }}>
          {fmtHHMM(startTime)} – {fmtHHMM(endTime)}
        </span>
        <span style={{ fontSize: 11, color: '#000', marginLeft: 'auto' }}>{durLabel(startTime, endTime)}</span>
      </div>
    );

    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: '20px 22px', maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>

          {/* Officer header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ background: badgeColor.bg, color: badgeColor.text, fontWeight: 700, fontSize: 13, padding: '3px 8px', borderRadius: 6 }}>
              {officerId}
            </span>
            {meta.name && <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{meta.name}</span>}
            {meta.deployment && <span style={{ fontSize: 12, color: '#000' }}>{meta.deployment}</span>}
            {/* Same cap as every other auto-assign entry point. */}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <MaxCounterDurationField compact />
              <HandoffOverlapToggle compact />
            </span>
          </div>

          {/* Info card */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>

            {/* Assign-only mode: just the green slots that will be filled. Existing
                counters are untouched, so they are not listed. */}
            {!regenShouldClear && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Slots to fill
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {vacantRanges.map((r, i) => (
                    <SummaryRow key={i} counter="—" startTime={r.start} endTime={r.end}
                      color={{ bg: '#dcfce7', text: '#16a34a' }} />
                  ))}
                </div>
              </div>
            )}

            {/* Clear mode: the availability window and the counters that will be cleared. */}
            {regenShouldClear && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 72 }}>Availability</span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {availSegs.map((s, i) => (
                      <span key={i} style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#1e293b', background: '#e2e8f0', padding: '2px 7px', borderRadius: 4 }}>
                        {fmtHHMM(s.start)}–{fmtHHMM(s.end)}
                      </span>
                    ))}
                    {availSegs.length === 0 && <span style={{ color: '#000', fontSize: 12 }}>—</span>}
                  </div>
                </div>
                {existingRows.length > 0 ? (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Current assignments — will be cleared
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {existingRows.map(r => (
                        <SummaryRow key={r.id} counter={counterLabel(r.counter)} startTime={r.startTime} endTime={r.endTime} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#000' }}>No existing assignments.</div>
                )}
              </>
            )}
          </div>

          {/* Opt-in: re-plan the whole availability window instead of just the green slots.
              Only offered when there is both green time to fill and counters to clear —
              with no green time, clearing is the only way to assign (see the panel above). */}
          {vacantRanges.length > 0 && existingRows.length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', marginBottom: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={regenShouldClear}
                onChange={e => setRegenShouldClear(e.target.checked)}
                style={{ accentColor: '#dc2626' }}
              />
              Clear existing counters and re-assign the whole availability window
            </label>
          )}

          {/* Break selection */}
          {showBreaks && (
            <div style={{ marginBottom: 14, padding: isOT ? '8px 10px' : 0, background: isOT ? '#fffbeb' : 'transparent', border: isOT ? '1px solid #fde68a' : 'none', borderRadius: isOT ? 8 : 0 }}>
              {isOT && (
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Break schedule
                </div>
              )}
              {!isOT && (
                <div style={{ fontSize: 10, fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Breaks
                </div>
              )}
              <div style={{ display: 'flex', gap: isOT ? 4 : 8, marginBottom: isOT ? 8 : 0 }}>
                {(['1st break', '2nd break', '3rd break'] as const).map((label, bIdx) => {
                  const slotsVal = regenBreakSizes[bIdx] ?? null;
                  const palette = slotsVal != null ? getDurationPalette(slotsVal * 15, 15) : null;
                  return (
                    <div key={bIdx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isOT ? 1 : 3 }}>
                      <span style={{ fontSize: isOT ? 9 : 10, color: isOT ? '#92400e' : '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{label}</span>
                      <select
                        value={slotsVal ?? ''}
                        onChange={e => {
                          const val = e.target.value === '' ? null : parseInt(e.target.value);
                          setRegenBreakSizes(prev => prev.map((s, i) => i === bIdx ? val : s));
                        }}
                        style={{
                          width: '100%', padding: isOT ? '2px 1px' : '6px 4px',
                          border: palette ? 'none' : (isOT ? '1px solid #fde68a' : '1px solid #e2e8f0'),
                          borderRadius: isOT ? 4 : 5, fontSize: isOT ? 10 : 12,
                          background: palette?.bg ?? 'white',
                          color: palette?.textColor ?? (isOT ? '#a16207' : '#94a3b8'),
                          fontWeight: palette ? 600 : 400,
                          cursor: 'pointer',
                        }}
                      >
                        <option value="">{isOT ? 'None' : 'Auto'}</option>
                        {BREAK_OPTIONS.map(opt => (
                          <option key={opt.slots} value={opt.slots}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              {/* OT-only: break window selectors */}
              {isOT && (() => {
                const windowSlots = timeSlots;
                return (
                  <div style={{ borderTop: '1px solid #fde68a', paddingTop: 7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 9, color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Earliest break starts from</div>
                        <select
                          value={regenBreakEarliestStart}
                          onChange={e => setRegenBreakEarliestStart(e.target.value)}
                          style={{ width: '100%', padding: '3px 4px', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12, background: 'white', color: '#78350f' }}
                        >
                          {windowSlots.map(t => <option key={t} value={t}>{fmtHHMM(t)}</option>)}
                        </select>
                      </div>
                      <span style={{ color: '#d97706', fontSize: 14, fontWeight: 700, paddingTop: 14 }}>–</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 9, color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Latest break ends by</div>
                        <select
                          value={regenBreakLatestEnd}
                          onChange={e => setRegenBreakLatestEnd(e.target.value)}
                          style={{ width: '100%', padding: '3px 4px', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12, background: 'white', color: '#78350f' }}
                        >
                          {windowSlots.map(t => <option key={t} value={t}>{fmtHHMM(t)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleConfirmRegen}
              disabled={regenerating}
              style={{ flex: 1, padding: '9px', background: regenShouldClear ? '#dc2626' : '#2563eb', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: regenerating ? 'not-allowed' : 'pointer', opacity: regenerating ? 0.6 : 1 }}
            >{regenerating ? 'Working…' : regenShouldClear ? 'Clear & Re-assign' : 'Assign'}</button>
            <button
              onClick={() => setShowRegenBreakConfig(false)}
              disabled={regenerating}
              style={{ flex: 1, padding: '9px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: regenerating ? 'not-allowed' : 'pointer' }}
            >Cancel</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Confirmation overlay for fully removing the officer (card + all counters)
  if (confirmDelete) {
    return createPortal(
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <div style={{ background: 'white', borderRadius: 12, padding: '24px 28px', maxWidth: 320, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Remove officer?</div>
          <div style={{ fontSize: 13, color: '#000', marginBottom: 20 }}>
            This deletes <b>{card.metadata.name || officerId}</b> and all their assigned counters from the roster. You can restore a previous version from Version History if you change your mind.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleConfirmDelete}
              disabled={deleting}
              style={{ flex: 1, padding: '8px 0', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1 }}
            >
              {deleting ? 'Removing…' : 'Remove officer'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              style={{ flex: 1, padding: '8px 0', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Confirmation overlay for clearing counters only (officer card stays, ready for
  // the press-and-hold flow to re-populate — mirrors the blank-row pen-and-paper workflow)
  if (confirmClearCounters) {
    return createPortal(
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <div style={{ background: 'white', borderRadius: 12, padding: '24px 28px', maxWidth: 320, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Clear all counters?</div>
          <div style={{ fontSize: 13, color: '#000', marginBottom: 20 }}>
            This removes every counter assignment for <b>{card.metadata.name || officerId}</b>. {card.metadata.name || officerId} stays on the roster with no counters assigned.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleConfirmClearCounters}
              disabled={clearingCounters}
              style={{ flex: 1, padding: '8px 0', background: '#d97706', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: clearingCounters ? 'not-allowed' : 'pointer', opacity: clearingCounters ? 0.6 : 1 }}
            >
              {clearingCounters ? 'Clearing…' : 'Clear counters'}
            </button>
            <button
              onClick={() => setConfirmClearCounters(false)}
              disabled={clearingCounters}
              style={{ flex: 1, padding: '8px 0', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }




  // ── WhatsApp contact picker ───────────────────────────────────────────────
  if (showWhatsAppPicker) {
    return createPortal(
      <div
        onClick={() => { closeWhatsAppFlow(); }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            maxHeight: '80vh' }}
        >
          {/* QR step — shown once a recipient is picked, instead of opening the
              wa.me link here directly. Clicking the code opens it on THIS
              device; scanning it with a phone opens it there instead, so
              whoever is at the roster can hand the send-off to whichever
              device should actually do it. */}
          {pendingRecipient ? (
            <WhatsAppQrStep
              phone={pendingRecipient.phone}
              message={pendingMessage}
              onMessageChange={setPendingMessage}
              recipientLabel={pendingRecipient.label}
              recipientPill={pendingRecipient.pill}
              onBack={() => setPendingRecipient(null)}
              onClose={closeWhatsAppFlow}
            />
          ) : (
          <>
          {/* Header */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WaIcon size={20} />
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Send to WhatsApp</span>
            </div>
            <button
              onClick={() => { closeWhatsAppFlow(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#000', padding: 0 }}
            >
              ✕
            </button>
          </div>

          <ContactPicker
            data={contacts}
            onPick={(r) => handleSendWhatsAppByPhone(r.phone, r.name ? `Hello ${r.name}` : 'Hello', r.label, r.pill)}
          />

          {/* Per-session message footer — shared by everyone in this session ID */}
          <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase',
              letterSpacing: '0.06em', marginBottom: 6 }}>
              Message footer (this session)
            </div>
            <textarea
              value={footerDraft ?? footerText}
              onChange={(e) => setFooterDraft(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7,
                fontSize: 12, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
            {footerDraft !== null && footerDraft !== footerText && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setFooterDraft(DEFAULT_WHATSAPP_FOOTER)}
                  style={{ padding: '5px 12px', background: '#f8f9fa', border: '1px solid #ddd',
                    borderRadius: 7, fontSize: 12, cursor: 'pointer' }}
                >
                  Use default
                </button>
                <button
                  disabled={footerSaving}
                  onClick={async () => {
                    setFooterSaving(true);
                    const ok = await saveFooter(footerDraft);
                    setFooterSaving(false);
                    if (ok) setFooterDraft(null);
                    else notifyError('Could not save the footer');
                  }}
                  style={{ padding: '5px 12px', background: '#0f7a3d', color: 'white', border: 'none',
                    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: footerSaving ? 'wait' : 'pointer' }}
                >
                  {footerSaving ? 'Saving…' : 'Save footer'}
                </button>
              </div>
            )}
          </div>

          {/* Footer — the QR step has its own "← Back" instead */}
          <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
            <button
              onClick={() => { closeWhatsAppFlow(); }}
              style={{ padding: '7px 16px', background: '#f8f9fa', border: '1px solid #ddd',
                borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
            >
              Back
            </button>
          </div>
          </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  const isSOS = card.metadata.officer_type === 'SOS';
  const isOT  = card.metadata.officer_type === 'OT';
  const availColor = isSOS ? WINDOW_COLORS.SOS : isOT ? WINDOW_COLORS.OT : WINDOW_COLORS.MAIN;
  const availBg    = isSOS ? '#fdf2f8' : isOT ? '#fffbeb' : '#eff6ff';

  // ── Inline assignment table ───────────────────────────────────────────────
  const renderTable = (
    segRows: AssignmentRow[],
    vacants: Array<{ start: string; end: string }>,
    addRowCb: (afterRowId?: string, overrideEnd?: string, overrideStart?: string) => void,
  ) => {
    type MergedItem =
      | { type: 'assign'; row: AssignmentRow }
      | { type: 'vacant'; start: string; end: string };
    const merged: MergedItem[] = [
      ...segRows.map(r => ({ type: 'assign' as const, row: r })),
      ...vacants.map(v => ({ type: 'vacant' as const, ...v })),
    ].sort((a, b) => {
      const aS = a.type === 'assign' ? hmmmToSlot(a.row.startTime, startHour) : hmmmToSlot(a.start, startHour);
      const bS = b.type === 'assign' ? hmmmToSlot(b.row.startTime, startHour) : hmmmToSlot(b.start, startHour);
      return aS - bS;
    });

    return (
      <div style={{ paddingBottom: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr 24px', gap: 3, padding: '4px 2px 3px', fontSize: 10, fontWeight: 700, color: availColor, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
          <div>Start</div><div>End</div><div>Counter</div><div>Notes</div><div/>
        </div>

        {merged.map((item, idx) => {
          if (item.type === 'vacant') {
            const prevAssign = [...merged.slice(0, idx)].reverse().find(m => m.type === 'assign');
            return (
              <Fragment key={`v-${item.start}`}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr 24px', gap: 3, padding: '4px 5px', alignItems: 'center', background: availBg, border: `1px dashed ${availColor}55`, borderRadius: 5, marginBottom: 2 }}>
                <span style={{ fontSize: 11, color: availColor, fontWeight: 600 }}>{fmtHHMM(item.start)}</span>
                <span style={{ fontSize: 11, color: availColor, fontWeight: 600 }}>{fmtHHMM(item.end)}</span>
                {!readOnlyCounters_ && (
                  <button onClick={handleAutoAssign} disabled={regenerating} style={{ fontSize: 9, fontWeight: 600, padding: '2px 5px', background: 'white', border: '1px solid #fde68a', color: '#78350f', borderRadius: 4, cursor: regenerating ? 'not-allowed' : 'pointer', opacity: regenerating ? 0.6 : 1, whiteSpace: 'nowrap' as const }}>{regenerating ? '⏳' : '⚡ Auto'}</button>
                )}
                <input
                  type="text"
                  disabled={readOnlyCounters_}
                  value={vacantNoteFor(item)}
                  placeholder="Note"
                  onChange={e => {
                    setVacantNotes(prev => ({ ...prev, [vacantNoteKey(item)]: e.target.value }));
                    setIsDirty(true);
                  }}
                  style={{ padding: '2px 4px', border: `1px solid ${availColor}40`, borderRadius: '4px', fontSize: '12px', background: readOnlyCounters_ ? '#f1f5f9' : 'white', minWidth: 0, width: '100%', boxSizing: 'border-box' as const, cursor: readOnlyCounters_ ? 'not-allowed' as const : 'text' }}
                />
                <div/>
              </div>
              {!readOnlyCounters_ && (
                <div style={{ padding: '0 2px 4px' }}>
                  <button
                    onClick={() => addRowCb(prevAssign?.type === 'assign' ? prevAssign.row.id : undefined, item.end, item.start)}
                    title="Assign a counter to this unassigned range"
                    style={{ width: '100%', fontSize: 12, fontWeight: 700, padding: '5px 8px', background: availColor, color: '#fff', border: `1px solid ${availColor}`, borderRadius: 6, cursor: 'pointer', lineHeight: 1.3, whiteSpace: 'nowrap' as const }}
                  >
                    + Add ctr
                  </button>
                </div>
              )}
              </Fragment>
            );
          }

          const row = item.row;
          const rowDirty = dirtyRowIds.has(row.id);
          const rowHighlighted = !rowDirty && row.id === highlightedRowId;
          const rowInvalid = !(isSOS || isOT) && !isValidTimeRange(row.startTime, row.endTime, shiftType);
          // A row inserted between two neighbors that already touch (no gap) gets
          // start === end — flagged on the time dropdowns regardless of officer
          // type, since rowInvalid above is skipped for SOS/OT.
          const isZeroDuration = !!row.startTime && !!row.endTime && row.startTime === row.endTime;
          const nextItem = merged[idx + 1];
          const immediateNext = nextItem?.type === 'assign' ? nextItem : null;
          const breakMins = immediateNext ? minutesBetween(row.endTime, immediateNext.row.startTime) : 0;
          const isOverlap = breakMins < 0;
          const hasBreak = breakMins > 0;
          const cellBg = rowInvalid ? '#fff0f0' : rowDirty ? '#fffde7' : rowHighlighted ? `${availColor}18` : 'transparent';
          const cellBorder = rowInvalid ? '2px solid #dc3545' : rowDirty ? '2px solid #f59e0b' : rowHighlighted ? `2px solid ${availColor}` : '2px solid #334155';
          const sel = {
            padding: '2px 4px', border: cellBorder, borderRadius: '4px', fontSize: '12px',
            background: readOnlyCounters_ ? '#f1f5f9' : (cellBg === 'transparent' ? '#fff' : cellBg), fontWeight: 600, minWidth: 0, width: '100%', boxSizing: 'border-box' as const,
            cursor: readOnlyCounters_ ? 'not-allowed' as const : 'pointer' as const,
            color: readOnlyCounters_ ? '#475569' : (rowInvalid ? '#dc3545' : '#000'),
          };
          const timeSel = isZeroDuration && !readOnlyCounters_
            ? { ...sel, border: '1px solid #dc3545', background: '#fff0f0', color: '#dc3545' }
            : sel;

          return (
            <div key={row.id}>
              <div
                onMouseEnter={() => { if (canHover()) pointAtRow(row); }}
                onMouseLeave={() => { if (canHover()) announceCounterFocus(null); }}
                onFocus={() => pointAtRow(row)}
                onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) announceCounterFocus(null); }}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr 24px', gap: 3, padding: '2px 2px', borderBottom: rowInvalid ? 'none' : '1px solid #f5f5f5', alignItems: 'center', background: cellBg, borderRadius: (rowDirty || rowInvalid || rowHighlighted) ? '4px 4px 0 0' : undefined }}>
                <select disabled={readOnlyCounters_} value={row.startTime} onChange={e => handleAssignmentChange(officerId, row.id, 'startTime', e.target.value)} style={timeSel}>
                  {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select disabled={readOnlyCounters_} value={row.endTime} onChange={e => handleAssignmentChange(officerId, row.id, 'endTime', e.target.value)} style={row.endTime === '' ? { ...timeSel, border: '2px solid #dc3545', background: '#fff0f0', color: '#dc3545' } : timeSel}>
                  {row.endTime === '' && <option value="" disabled>End</option>}
                  {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <CounterChoiceSelect
                  disabled={readOnlyCounters_}
                  value={choiceOf(row)}
                  onChange={(choice) => handleChoiceChange(row.id, choice)}
                  numCounters={numCounters}
                  operationMode={operation_mode}
                  style={sel}
                  vacancyOf={(n) => rowVacancy(row, n)}
                  slotTime={(slot) => exportSlotToTime(slot, startHour)}
                  neededUntilSlot={rowEndSlot(row)}
                  title={`Counter — ${row.startTime}–${row.endTime || '…'}`}
                />
                <input
                  type="text"
                  disabled={readOnlyCounters_}
                  value={row.notes ?? ''}
                  placeholder="Note"
                  onChange={e => handleAssignmentChange(officerId, row.id, 'notes', e.target.value)}
                  style={sel}
                />
                <button
                  disabled={readOnlyCounters_}
                  onClick={() => handleDeleteRow(officerId, row.id)}
                  style={{ background: 'none', border: 'none', cursor: readOnlyCounters_ ? 'not-allowed' : 'pointer', color: readOnlyCounters_ ? '#94a3b8' : '#dc3545', opacity: readOnlyCounters_ ? 0.5 : 1, fontSize: 13, padding: 0, lineHeight: 1 }}
                >✕</button>
              </div>
              {rowInvalid && <div style={{ background: '#fff0f0', borderBottom: '1px solid #f5f5f5', borderRadius: '0 0 4px 4px', color: '#dc3545', fontSize: 10, padding: '1px 4px 3px' }}>End must be after start</div>}
              {row.endTime === '' && <div style={{ background: '#fff0f0', borderBottom: '1px solid #f5f5f5', borderRadius: '0 0 4px 4px', color: '#dc3545', fontSize: 10, padding: '1px 4px 3px' }}>Select an end time</div>}
              {!rowInvalid && isZeroDuration && <div style={{ background: '#fff0f0', borderBottom: '1px solid #f5f5f5', borderRadius: '0 0 4px 4px', color: '#dc3545', fontSize: 10, padding: '1px 4px 3px' }}>Counter duration too short</div>}
              {/* Per-row "add after this row" — kept in the same grid columns as the row
                  above it so the button always lands under the Notes column, whether or
                  not the break badge (which occupies the columns to its left) is shown. */}
              {(!readOnlyCounters_ || hasBreak || isOverlap) && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 2px 4px' }}>
                  {(hasBreak || isOverlap) && (
                    <div style={{ flexShrink: 0 }}>
                      <BreakDurationBadge minutes={breakMins} isOverlap={isOverlap} compact />
                    </div>
                  )}
                  {!readOnlyCounters_ && (
                    <button
                      onClick={() => addRowCb(row.id, immediateNext ? immediateNext.row.startTime : undefined)}
                      title="Add counter after this row"
                      style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, padding: '5px 8px', background: availColor, color: '#fff', border: `1px solid ${availColor}`, borderRadius: 6, cursor: 'pointer', lineHeight: 1.3, whiteSpace: 'nowrap' as const }}
                    >
                      + Add ctr
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {segRows.length === 0 && vacants.length === 0 && (
          <div style={{ padding: '8px 4px', fontSize: 11, color: '#000', textAlign: 'center' }}>No counter assignments — add a row below</div>
        )}
        {!readOnlyCounters_ && segRows.length === 0 && (
          <button onClick={() => addRowCb()} style={{ marginTop: 6, fontSize: 11, padding: '3px 8px', background: availBg, color: availColor, border: `1px solid ${availColor}40`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>+ Add counter</button>
        )}
      </div>
    );
  };

  // Restore is redundant when the officer is on the full shift and no counter differs from the roster as generated.
  const restoreRedundant = (() => {
    if (isDirty || !initialSchedule) return false;
    const cur = currentSchedule;
    if (!cur) return false;
    const fullSlots = timeSlots.length - 1;
    let covered = 0;
    try {
      for (const seg of editableSegs) covered += Math.max(0, hmmmToEndSlot(seg.end, startHour) - hmmmToSlot(seg.start, startHour));
    } catch { return false; }
    if (covered < fullSlots) return false;
    const n = Math.max(cur.length, initialSchedule.length);
    for (let i = 0; i < n; i++) if ((cur[i] ?? 0) !== (initialSchedule[i] ?? 0)) return false;
    return true;
  })();

  // ── Window card renderer ──────────────────────────────────────────────────
  // Wraps a renderTable output in a card with the availability window header.
  const renderWindowCard = (
    eSeg: { start: string; end: string },
    si: number,
    segAssignments: AssignmentRow[],
    vacantRanges: Array<{ start: string; end: string }>,
    addRowForSeg: (afterRowId?: string, overrideEnd?: string, overrideStart?: string) => void,
    isMultiSeg: boolean,
  ) => {
    return (
      <div key={si} style={{ border: `1px solid ${availColor}25`, borderRadius: 7, marginBottom: 6 }}>
        {/* Card header: <AVAIL> + time dropdowns + hours pill + actions */}
        <div style={{ background: availBg, borderBottom: `1px solid ${availColor}15`, borderRadius: '7px 7px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px 1px' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: availColor, letterSpacing: '0.06em', flexShrink: 0 }}>&lt;AVAIL&gt;</span>
            {/* Time range dropdowns */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, background: 'white', border: `1px solid ${availColor}40`, borderRadius: 4, padding: '0 3px', flex: 1, minWidth: 0 }}>
              <select
                disabled={!canEdit}
                value={eSeg.start}
                onChange={e => {
                  const u = editableSegs.map((s, i) => i === si ? { ...s, start: e.target.value } : s);
                  // Merge only on genuine overlap with the previous window — touching
                  // exactly (no gap) is what a split produces and must stay distinct;
                  // see mergeAvailabilitySegments.
                  updateSegs(mergeAvailabilitySegments(u, startHour));
                }}
                style={{ fontSize: 12, fontWeight: 700, color: '#000', background: '#fff', border: '2px solid #334155', borderRadius: 4, cursor: canEdit ? 'pointer' : 'not-allowed', padding: '1px 2px', outline: 'none', minWidth: 0, flex: 1 }}
              >
                {timeSlots.map(t => <option key={t} value={t}>{fmtHHMM(t)}</option>)}
              </select>
              <span style={{ color: availColor, fontSize: 10, flexShrink: 0 }}>–</span>
              <select
                disabled={!canEdit}
                value={eSeg.end}
                onChange={e => {
                  const u = editableSegs.map((s, i) => i === si ? { ...s, end: e.target.value } : s);
                  // Merge only on genuine overlap with the next window — touching
                  // exactly (no gap) is what a split produces and must stay distinct;
                  // see mergeAvailabilitySegments.
                  updateSegs(mergeAvailabilitySegments(u, startHour));
                }}
                style={{ fontSize: 12, fontWeight: 700, color: '#000', background: '#fff', border: '2px solid #334155', borderRadius: 4, cursor: canEdit ? 'pointer' : 'not-allowed', padding: '1px 2px', outline: 'none', minWidth: 0, flex: 1 }}
              >
                {timeSlots.map(t => <option key={t} value={t}>{fmtHHMM(t)}</option>)}
              </select>
              {canEdit && (
                <button
                  onClick={() => deleteAvailWindow(si)}
                  title="Delete this availability window"
                  style={{ background: 'none', border: 'none', color: availColor, cursor: 'pointer', padding: '0 2px', lineHeight: 1, opacity: 0.6, flexShrink: 0, display: 'flex', alignItems: 'center' }}
                ><TrashIcon /></button>
              )}
            </span>
            {/* Hours pill — inline after dropdowns, based on availability window span */}
            <DurationPill
              minutes={Math.max(0, hmmmToEndSlot(eSeg.end, startHour) - hmmmToSlot(eSeg.start, startHour)) * 15}
              color={availColor}
            />
            {/* Restore: only a main officer has a fixed (printed) schedule to go back to */}
            {si === 0 && canEdit && card.metadata.officer_type === 'MAIN' && (
              <button
                onClick={handleRestoreFull}
                disabled={regenerating || restoreRedundant}
                title={restoreRedundant ? 'Already on the full shift with no counter changes' : "Put this officer back on the printed roster (the uploaded roster's counters) for the whole shift"}
                style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', background: restoreRedundant ? '#f1f5f9' : 'white', border: `1px solid ${restoreRedundant ? '#cbd5e1' : availColor + '60'}`, color: restoreRedundant ? '#94a3b8' : availColor, borderRadius: 4, cursor: regenerating || restoreRedundant ? 'not-allowed' : 'pointer', opacity: regenerating ? 0.6 : 1, whiteSpace: 'nowrap' as const, flexShrink: 0 }}
              >{regenerating ? '⏳' : '↺ Restore'}</button>
            )}
            {savingAvail && <span style={{ fontSize: 9, color: availColor, opacity: 0.6, flexShrink: 0 }}>…</span>}
            {/* Per-segment copy/WA buttons for multi-segment */}
            {isMultiSeg && (
              <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                <button
                  title="Preview & copy"
                  onClick={() => setPreview({ text: buildScheduleText([eSeg]), segIdx: si })}
                  style={{ background: copiedSegIdx === si ? '#d1fae5' : 'transparent', border: `1px solid ${copiedSegIdx === si ? '#6ee7b7' : availColor + '50'}`, color: copiedSegIdx === si ? '#065f46' : availColor, borderRadius: 4, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                >
                  <CopyIcon copied={copiedSegIdx === si} />
                </button>
                <button
                  title="Send via WhatsApp"
                  onClick={() => handleOpenWhatsAppPicker(buildScheduleText([eSeg]))}
                  style={{ background: 'transparent', border: '1px solid #86efac', borderRadius: 4, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                >
                  <WaIcon size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Card body — same tint as header for uniform color per officer type */}
        <div style={{ padding: '0 6px', background: availBg, borderRadius: '0 0 7px 7px' }}>
          {renderTable(segAssignments, vacantRanges, addRowForSeg)}
        </div>
      </div>
    );
  };

  // ── Identity + actions row — the officer type pill, id, deployment (SOS), and the
  // WhatsApp/copy/pin/trash actions. Portaled into the shared multi-card panel's own
  // title bar when there's exactly one card open (headerAnchor set), so it shares a
  // row with that panel's maximize/close buttons instead of sitting below a redundant
  // "Officer Schedule" label — saving a full row of vertical space. Falls back to
  // rendering inline (below) when there's no anchor: standalone mode builds its own
  // outer header around it directly; asCard mode with more than one card stacked (no
  // single anchor to target unambiguously) renders it as this card's own top row.
  // The colour this officer is drawn in, which a user may have switched by hand (colour only). Read off
  // the live roster, not the card: the card is the popup's own copy, taken when it opened.
  const colourAs = gridData.officer_metadata[officerId]?.colour_as ?? null;
  const pink = (colourAs ?? card.metadata.officer_type) === 'SOS';
  const identityAndActionsRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
      {isOT ? (
        <span style={{ fontSize: '12px', fontWeight: 800, padding: '2px 8px', borderRadius: '99px', flexShrink: 0, background: '#FDFD96', color: '#713f12' }}>
          {card.metadata.officer_type}
        </span>
      ) : (
        // The type pill is the colour switch: a tap draws the officer in the other type's colour.
        // Colour only — they stay the type the pill names.
        <button
          type="button"
          disabled={!canEdit}
          data-testid="officer-colour-toggle"
          onClick={() => {
            const next = pink ? 'MAIN' : 'SOS';
            apiClient.setOfficerColour(officerId, next === card.metadata.officer_type ? null : next)
              .catch(() => notifyError('Could not change the colour'));
          }}
          title={canEdit
            ? `Tap to show ${officerId} as ${pink ? 'MAIN' : 'SOS'}`
            : undefined}
          style={{
            fontSize: '12px', fontWeight: 800, padding: '2px 8px', borderRadius: '99px', flexShrink: 0,
            background: pink ? '#ffc6d9' : '#a2d2ff', color: pink ? '#9d174d' : '#1e40af',
            border: 'none', cursor: canEdit ? 'pointer' : 'default', lineHeight: 1.2,
          }}
        >
          {pink ? 'SOS' : 'MAIN'}
        </button>
      )}
      {/* The id is the one elastic thing here: a long key (e.g. OT1_1000-1030) on a
          narrow phone would otherwise overflow the row and shove the maximize/close
          buttons out of alignment, so it truncates instead of pushing. */}
      <span title={officerId} style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, color: '#333', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {officerId}
      </span>
      {isSOS && (
        <input type="text" value={card.metadata.deployment ?? ''} placeholder="Deployment"
          readOnly={!canEdit}
          onChange={(e) => handleMetadataChange(officerId, 'deployment', e.target.value)}
          style={{ padding: '3px 5px', border: isDirty ? '1px solid #f59e0b' : '1px solid #ddd', borderRadius: '4px', fontSize: '13px', fontWeight: 600, background: !canEdit ? '#f1f5f9' : isDirty ? '#fffde7' : '#fafafa', minWidth: 0, flex: 1, boxSizing: 'border-box' as const, cursor: canEdit ? 'text' : 'default' }} />
      )}
      <div style={{ flex: isSOS ? undefined : 1 }} />
      <button title="Send via WhatsApp" onClick={() => handleOpenWhatsAppPicker()}
        style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '5px', cursor: 'pointer', padding: '4px 9px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <WaIcon size={16} />
      </button>
      <button title="Preview & copy schedule" aria-label="Preview & copy schedule" data-testid="schedule-preview-open" onClick={() => setPreview({ text: buildScheduleText() })}
        style={{ background: copied ? '#d1fae5' : '#f8fafc', border: '1px solid #000', color: copied ? '#065f46' : '#475569', borderRadius: '5px', cursor: 'pointer', padding: '5px 11px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <CopyIcon copied={copied} />
      </button>
      <button
        title={isPinned ? 'Unpin' : 'Pin (keep visible when clicking other officers)'}
        onClick={() => isPinned ? onUnpin?.() : onPin?.()}
        style={{ background: isPinned ? '#eff6ff' : 'none', border: '1px solid #000', borderRadius: '5px', cursor: 'pointer', padding: '4px 9px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: isPinned ? '#2563eb' : '#94a3b8' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="17" x2="12" y2="22"/>
          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
        </svg>
      </button>
      <button title={canEdit ? "Remove officer entirely" : "View only — switch to Edit to remove"} disabled={!canEdit} onClick={() => handleDeleteCard(officerId)} style={{ background: 'none', border: '1px solid #000', color: '#dc3545', borderRadius: '5px', cursor: canEdit ? 'pointer' : 'not-allowed', fontSize: '14px', padding: '5px 8px', lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: canEdit ? 1 : 0.5 }}>🗑</button>
      {preview && createPortal(
        <div
          onClick={() => setPreview(null)}
          // dvh = the area actually visible: on iPhone Safari 100vh runs under the bottom toolbar, hiding the buttons.
          // (A browser without dvh drops the height and keeps inset: 0.)
          style={{ position: 'fixed', inset: 0, height: '100dvh', background: 'rgba(0,0,0,0.55)', zIndex: 4600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
            padding: isDesktop
              ? 'max(12px, 3vmin)'
              : 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))' }}
        >
          <div
            role="dialog" aria-label="Schedule preview" data-testid="schedule-preview"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%',
              // Phone / iPad: the compact card. Desktop: fills the window so the text can be read off the screen.
              ...(isDesktop ? { height: '100%' } : { maxWidth: 420, maxHeight: '100%' }) }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 800, fontSize: 17, color: '#1e293b', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>{isOT ? 'OT' : pink ? 'SOS' : 'MAIN'} {officerId}</span>
              {isDesktop && (
                <button onClick={() => setPreview(null)} aria-label="Close" title="Close"
                  style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#64748b', padding: '0 4px' }}
                >✕</button>
              )}
            </div>
            {isDesktop ? (
              /* Fills the window at the largest size that fits (never smaller than a 5-line text), so it can be
                 photographed without zooming. */
              <FitText
                text={preview.text || 'No counter assignments.'}
                minLines={5}
                style={{ ...schedulePreStyle, flex: 1, minHeight: 0, overflowY: 'auto', lineHeight: 1.4 }}
              />
            ) : (
              <pre style={{ ...schedulePreStyle, overflowY: 'auto', fontSize: 18, lineHeight: 1.5 }}>
                {preview.text || 'No counter assignments.'}
              </pre>
            )}
            <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
              <button
                data-testid="schedule-preview-copy"
                onClick={() => handleCopySchedule(preview.text, preview.segIdx)}
                disabled={!preview.text}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                  background: (preview.segIdx !== undefined ? copiedSegIdx === preview.segIdx : copied) ? '#059669' : '#4f46e5', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <CopyIcon copied={preview.segIdx !== undefined ? copiedSegIdx === preview.segIdx : copied} />
                {(preview.segIdx !== undefined ? copiedSegIdx === preview.segIdx : copied) ? 'Copied' : 'Copy text'}
              </button>
              {!isDesktop && <button
                onClick={() => setPreview(null)}
                style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', color: '#334155' }}
              >Close</button>}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );

  // ── Card header — the name + Clear-all row, shared between asCard and standalone
  // modes. The identity/actions row above lives separately (see identityAndActionsRow)
  // so it can be portaled out instead of always nesting here.
  // Explicit px math (not `em`) so the Clear-all button below can reuse the exact
  // same height even though it renders at a different font-size than the name field.
  const NAME_ROW_HEIGHT = 'calc(1.3 * 14px * 1.5 + 8px)';
  const cardHeader = (
    <div style={{ flexShrink: 0 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch', padding: '5px 6px 5px 12px', borderBottom: '1px solid #e5e7eb' }}>
      <textarea value={card.metadata.name} placeholder="Name" rows={1}
        readOnly={!canEdit}
        onChange={(e) => handleMetadataChange(officerId, 'name', e.target.value)}
        style={{
          padding: '3px 6px', border: isDirty ? '1px solid #f59e0b' : '1px solid #ddd', borderRadius: '4px',
          fontSize: '14px', fontWeight: 600, background: !canEdit ? '#f1f5f9' : isDirty ? '#fffde7' : '#fafafa',
          cursor: canEdit ? 'text' : 'default',
          flex: 1, minWidth: 0, boxSizing: 'border-box' as const, resize: 'none', fontFamily: 'inherit',
          lineHeight: '1.3', height: NAME_ROW_HEIGHT, maxHeight: NAME_ROW_HEIGHT,
          overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }} />
      {!readOnlyCounters_ && editableSegs.length > 1 && (
        <button
          onClick={handleAutoAssign}
          disabled={regenerating}
          title="Auto-assign all unassigned windows"
          style={{
            flexShrink: 0, height: NAME_ROW_HEIGHT, boxSizing: 'border-box' as const,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '2px 10px', background: 'white', border: '1px solid #fde68a', color: '#78350f',
            borderRadius: 5, cursor: regenerating ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600,
            opacity: regenerating ? 0.6 : 1, whiteSpace: 'nowrap' as const,
          }}
        >
          {regenerating ? '⏳' : '⚡ Assign all'}
        </button>
      )}
      {!readOnlyCounters_ && card.assignments.length > 0 && (
        <button
          title="Clear all counter assignments for this officer"
          onClick={() => handleClearCounters(officerId)}
          style={{
            flexShrink: 0, width: 64, height: NAME_ROW_HEIGHT, boxSizing: 'border-box' as const,
            fontSize: 10, fontWeight: 700, lineHeight: 1.25, letterSpacing: '0.02em',
            background: `${availColor}15`, color: '#dc3545', border: `1.5px solid ${availColor}`, borderRadius: 5,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center' as const, padding: '2px 4px',
          }}
        >
          CLEAR ALL CTRS
        </button>
      )}
      </div>
    </div>
  );

  const identityPortal = headerAnchor ? createPortal(identityAndActionsRow, headerAnchor) : null;

  // ── Inner body — same in both asCard and standalone modes ──────────────────
  const innerBody = (
    <div style={{ padding: '4px 10px' }}>
      {(() => {
        const fullShiftSlots = timeSlots.length - 1;
        const totalAvailSlots = editableSegs.reduce((sum, seg) => {
          try { return sum + Math.max(0, hmmmToEndSlot(seg.end, startHour) - hmmmToSlot(seg.start, startHour)); }
          catch { return sum; }
        }, 0);
        const isFullShift = totalAvailSlots >= fullShiftSlots;

        const addWindowBtn = !canEdit ? null : (
          <button
            onClick={() => {
              const lastSeg = editableSegs[editableSegs.length - 1];
              // Start the new window one slot AFTER the previous window's end, leaving a gap
              // so it is a genuinely separate availability window. Defaulting to the previous
              // end (adjacent) would union straight back into it (touching windows merge).
              let newStart = timeSlots[0];
              if (lastSeg) {
                const gapSlot = hmmmToEndSlot(lastSeg.end, startHour) + 1;
                newStart = gapSlot < timeSlots.length ? timeSlots[gapSlot] : lastSeg.end;
              }
              const u = [...editableSegs, { start: newStart, end: timeSlots[timeSlots.length - 1] }];
              updateSegs(u);
            }}
            style={{ marginTop: 4, fontSize: 11, padding: '5px 8px', background: availBg, color: availColor, border: `1px dashed ${availColor}50`, borderRadius: 5, cursor: 'pointer', fontWeight: 500, width: '100%', boxSizing: 'border-box' as const, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            + Add availability window
          </button>
        );

        if (editableSegs.length > 1) {
          return (
            <>
              {editableSegs.map((eSeg, si) => {
                const segAssignments = rowsInWindow(card.assignments, eSeg, startHour);
                const vacantRanges = getVacantRanges(eSeg, segAssignments);
                // `afterRowId`/`overrideEnd` let the per-row "+ Add counter" button insert
                // between two existing rows instead of always appending at the segment tail.
                const addRowForSeg = (afterRowId?: string, overrideEnd?: string, overrideStart?: string) => {
                  pushUndo(card.assignments);
                  const newId = rowId();
                  const afterId = afterRowId ?? segAssignments[segAssignments.length - 1]?.id;
                  const prevRow = afterId ? card.assignments.find(a => a.id === afterId) : undefined;
                  const startTime = overrideStart ?? (prevRow ? prevRow.endTime : eSeg.start);
                  // End left blank for the user to fill in; the window then grows to it (see handleAddRow).
                  const endTime = overrideEnd ?? '';
                  const carriedNote = endTime ? noteForNewRow(startTime, endTime) : undefined;
                  setCard(prev => {
                    const insertAt = afterId ? prev.assignments.findIndex(a => a.id === afterId) + 1 : prev.assignments.length;
                    const newRow: AssignmentRow = { id: newId, startTime, endTime, counter: '', notes: carriedNote };
                    const next = [...prev.assignments]; next.splice(insertAt, 0, newRow);
                    return { ...prev, assignments: next };
                  });
                  setIsDirty(true);
                  setDirtyRowIds(prev => new Set(prev).add(newId));
                };
                return renderWindowCard(eSeg, si, segAssignments, vacantRanges, addRowForSeg, true);
              })}
              {!isFullShift && addWindowBtn}
            </>
          );
        }

        if (editableSegs.length === 1) {
          const eSeg = editableSegs[0];
          const singleVacant = getVacantRanges(eSeg, card.assignments);
          return (
            <>
              {renderWindowCard(eSeg, 0, card.assignments, singleVacant, (afterRowId, overrideEnd) => handleAddRow(officerId, afterRowId, overrideEnd, eSeg.start), false)}
              {!isFullShift && addWindowBtn}
            </>
          );
        }

        return (
          <>
            {renderTable(card.assignments, [], (afterRowId, overrideEnd) => handleAddRow(officerId, afterRowId, overrideEnd))}
            {!isFullShift && addWindowBtn}
          </>
        );
      })()}
    </div>
  );

  // ── Footer — no Share section (copy+WA moved to card header) ──────────────
  const footerEl = (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center', padding: '5px 10px', borderTop: '1px solid #e5e7eb' }}>
      {undoStack.length > 0 && (
        <button
          onClick={handleUndoLocal}
          title="Undo last change (Ctrl+Z)"
          style={{ padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#000', lineHeight: 1 }}
        >↩</button>
      )}
    </div>
  );

  // ── asCard mode: card content only, no backdrop/panel shell ───────────────
  if (asCard) {
    return (
      <div style={{ borderBottom: '1px solid #f0f4f8', display: 'flex', flexDirection: 'column' }}>
        {identityPortal}
        {!headerAnchor && (
          <div style={{ display: 'flex', padding: '5px 6px 5px 12px', borderBottom: '1px solid #e5e7eb' }}>
            {identityAndActionsRow}
          </div>
        )}
        <div style={{ zoom: UI_SCALE }}>{cardHeader}</div>
        <div style={{ overflowY: 'auto' }}>
          <div style={{ zoom: UI_SCALE }}>
            {innerBody}
            {footerEl}
          </div>
        </div>
      </div>
    );
  }

  // ── Standalone mode: full panel with backdrop ──────────────────────────────
  return (
    <div
      className="officer-edit-panel-backdrop"
      onClick={handleRequestClose}
    >
      <div
        className={`officer-edit-panel${expanded ? ' expanded' : ''}${fromBottom ? ' officer-edit-panel--bottom' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Outer panel header: identity/actions row + expand + close, sharing one row
            instead of a separate "Officer Schedule" title bar above it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0, background: 'white' }}>
          {identityAndActionsRow}
          <button onClick={() => setExpanded(e => !e)} title={expanded ? 'Minimize' : 'Expand'} className="popup-expand-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#000', lineHeight: 1, padding: '0 2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {expanded ? (
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="11,5 7,5 7,1"/>
                <line x1="11" y1="1" x2="7" y2="5"/>
                <polyline points="1,7 5,7 5,11"/>
                <line x1="1" y1="11" x2="5" y2="7"/>
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="7,1 11,1 11,5"/>
                <line x1="11" y1="1" x2="6.5" y2="5.5"/>
                <polyline points="5,11 1,11 1,7"/>
                <line x1="1" y1="11" x2="5.5" y2="6.5"/>
              </svg>
            )}
          </button>
          <button onClick={handleRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#000', lineHeight: 1, padding: '6px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        {!canEdit && <ViewOnlyBanner compact />}
        <div style={{ zoom: UI_SCALE }}>{cardHeader}</div>
        {/* Body + footer — scrollable */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ zoom: UI_SCALE }}>
            {innerBody}
            {footerEl}
          </div>
        </div>
      </div>
    </div>
  );
}
